import * as pc from 'playcanvas';
import { Vector3 } from 'three';
import {
  COLLISION_MOVE_SPEED,
  COLLISION_SLOW_SPEED,
  COLLISION_SPRINT_SPEED,
  CollisionSystem,
  type CollisionSceneTransform,
} from '@viewer/collision/CollisionSystem';
import { TouchControls, isTouchPrimaryDevice } from '@viewer/controls/TouchControls';
import { isStreamedSplatUrl } from '@viewer/lod/resolvePlayCanvasUrl';
import {
  PLAYCANVAS_DESKTOP_PRESET,
  MOBILE_LOD_SPLAT_COUNT,
  DESKTOP_LOD_SPLAT_COUNT,
  PLAYCANVAS_MOBILE_PRESET,
  PLAYCANVAS_STREAMING_FILL,
  mergePlayCanvasLodPreset,
  type PlayCanvasLodPreset,
} from '@viewer/lod/streamingProfiles';
import { loadPcvPreview } from '@viewer/effects/loadPcvPreview';
import { ENTRANCE_DOT_COUNT, subsamplePcvPositions } from '@viewer/effects/subsamplePcv';
import {
  createCanvasLayoutSyncState,
  shouldSyncCanvasLayout,
} from '@viewer/playcanvas/canvasLayoutSync';
import {
  shouldApplyCanvasLayoutOnFrame,
  shouldEmitViewerHud,
  shouldSkipCanvasIntersectionGate,
  shouldUpdateInteractableHover,
  shouldUpdateWorldChrome,
  shouldUpdateWorldZones,
} from '@viewer/playcanvas/viewerFrameWork';
import { loadLogoFormation, type LogoFormation } from '@viewer/playcanvas/entranceDotChoreography';
import { canMoveCamera, canUpdateCameraLook } from '@viewer/playcanvas/entranceMotion';
import { EntranceHud } from '@viewer/playcanvas/EntranceHud';
import {
  canFullyDissolvePreviewDots,
  previewDissolveWaveRadius,
  resolveEntranceDisplayProgress,
  resolveEntranceRevealMode,
  canReleaseReveal,
  canStartPreviewDots,
  latchWorldReady,
  shouldAllowStreamingRevealTimeout,
  shouldFallbackWarmEntrance,
} from '@viewer/playcanvas/entrancePhaseGuards';
import {
  entranceTrace,
  entranceTraceThrottled,
  resetEntranceTrace,
} from '@viewer/playcanvas/entranceTrace';
import { lookAnglesFromForward } from '@viewer/playcanvas/lookAngles';
import type { MobileQualityRuntimeOptions } from '@viewer/playcanvas/mobileQuality';
import type { CameraAuthoringSnapshot } from '@viewer/playcanvas/cameraAuthoring';
import { capSplatBudgetForGraphics } from '@viewer/playcanvas/gsplatBudget';
import {
  detectIosWebKit,
  resolveGraphicsDeviceOrder,
  type GraphicsDeviceKind,
} from '@viewer/playcanvas/graphicsDevicePolicy';
import {
  createStreamedSplatProgressState,
  estimateStreamedSplatFileProgress,
  observeStreamedSplatProgressAssets,
  resetStreamedSplatProgressState,
  resolveLodByteTarget,
  resolveStreamedSplatDownloadProgress,
  summarizeStreamedSplatProgress,
} from '@viewer/playcanvas/streamedSplatProgress';
import {
  revealCenterSpaceForRenderer,
  type RevealCenterSpace,
} from '@viewer/playcanvas/revealCenterSpace';
import {
  SPATIAL_CONTROL_DEMO_LOOK_OFFSET_DEG,
  SPATIAL_CONTROL_DEMO_MOVE_OFFSET_M,
  SPATIAL_CONTROL_DEMO_TOUCH_LOOK_OFFSET_DEG,
  SPATIAL_CONTROL_DEMO_TOUCH_MOVE_OFFSET_M,
  SpatialControlPrompt,
  type SpatialControlDemoActionSample,
  type SpatialControlDemoState,
} from '@viewer/playcanvas/spatialControlPrompt';
import { PlayCanvasFlythroughController } from '@viewer/playcanvas/PlayCanvasFlythroughController';
import { PlayCanvasPointDots } from '@viewer/playcanvas/PlayCanvasPointDots';
import { WorldEntrance, type WorldEntrancePhase } from '@viewer/playcanvas/WorldEntrance';
import { PlayCanvasWorldContent } from '@viewer/playcanvas/PlayCanvasWorldContent';
import { MeasureOverlay } from '@viewer/overlay/MeasureOverlay';
import { photoLensToFov, type PhotoLens } from '@viewer/overlay/photoLens';
import { PhotoOverlay, type PhotoAspect } from '@viewer/overlay/PhotoOverlay';
import {
  detectTablet,
  isDiagnosticsEnabled,
  readBuildId,
  ViewerDiagnostics,
} from '@viewer/playcanvas/ViewerDiagnostics';
import type { LoadedScene } from '@viewer/world/worldTypes';

/** Self-hosted Draco — PlayCanvas defaults to /draco.wasm.wasm which 404s to index.html in dev. */
const PLAYCANVAS_DRACO = {
  jsUrl: '/vendor/draco/gltf/draco_wasm_wrapper.js',
  wasmUrl: '/vendor/draco/gltf/draco_decoder_gltf.wasm',
  numWorkers: 2,
} as const;

export interface PlayCanvasHudSnapshot {
  loadState: 'idle' | 'loading' | 'ready' | 'error';
  renderedSplats: number;
  splatBudget: number;
  lodRangeMin: number;
  lodRangeMax: number;
  streamingPhase: 'booting' | 'streaming' | 'full';
  streamingFill: number;
  loadProgress: number;
  entrancePhase: WorldEntrancePhase;
  entranceProgress: number;
  entranceDotsFill: number;
  worldReady: boolean;
  collisionLoaded: boolean;
  pointerLocked: boolean;
  touchMode: boolean;
  droneMode: boolean;
  fps: number;
  frameTimeMs: number;
  renderTimeMs: number;
  overlayRenderMs: number;
  devicePixelRatio: number;
  gsplatWorkMs: number;
  renderHistory: number[];
  graphicsApi: 'webgpu' | 'webgl2';
  /** Active gsplat renderer after platform fallback. */
  gsplatRenderer: 'compute' | 'raster-gpu' | 'raster-cpu' | 'auto';
  /** PlayCanvas engine frame stats (see app.stats). */
  engineFrameMs: number;
  drawCallsTotal: number;
  drawCallsForward: number;
  gsplatSortMs: number;
  gsplatBufferCopyPct: number;
  vramMb: number;
  forwardRenderMs: number;
  errorMessage?: string;
  /** Bootstrap log lines (tablet / ?diag=1). */
  diagLines?: readonly string[];
  diagEnabled?: boolean;
  canvasSize?: { w: number; h: number };
  bootstrapComplete?: boolean;
  cameraAuthoring?: CameraAuthoringSnapshot;
}

export interface MountPlayCanvasViewerOptions {
  splatUrl: string;
  preset?: PlayCanvasLodPreset;
  cameraStart?: [number, number, number];
  cameraYawDeg?: number;
  collisionUrl?: string;
  sceneTransform?: CollisionSceneTransform;
  worldScene?: LoadedScene;
  /** Element whose size drives canvas layout (e.g. fullscreen shell). */
  layoutRoot?: HTMLElement;
  onHud?: (snapshot: PlayCanvasHudSnapshot) => void;
  /** Fires once when entrance completes and the user can explore. */
  onWorldReady?: () => void;
  /** Called when measure tool requests full exit (Escape while idle). */
  onMeasureRequestExit?: () => void;
  /** Used for photo download filenames. */
  worldId?: string;
  /** Mobile quality cap, expressed as rendered splat millions. */
  initialQuality?: MobileQualityRuntimeOptions;
  /** Whether loaded video panels should autoplay or wait for a user tap. */
  initialMediaAutoplay?: boolean;
  /** Route/layout-level mobile signal, used to keep touch-first HUD affordances visible. */
  mobileLayout?: boolean;
}

export interface PlayCanvasViewer {
  resize: () => void;
  setDebugPerf: (enabled: boolean) => void;
  setOverlayTool: (tool: 'none' | 'measure') => void;
  toggleMeasureEdit: () => void;
  setQuality: (quality: MobileQualityRuntimeOptions) => void;
  setMediaAutoplay: (enabled: boolean) => void;
  getCameraAuthoringSnapshot: () => CameraAuthoringSnapshot | undefined;
  capturePng: (opts?: { aspect?: 'screen' | '1:1' | '4:3' | '16:9' | '9:16' }) => Promise<Blob>;
  dispose: () => void;
}

const DRONE_MOVE_SPEED = 14;
const FLY_SPEED = 4;
const TOUCH_LOOK_SENSITIVITY = 0.0042;
const PITCH_LIMIT = Math.PI / 2 - 0.02;
const RAD_TO_DEG = 180 / Math.PI;

// Render-rate cap. A slow architectural/real-estate walkthrough gains nothing perceptible
// above 30fps, and the per-frame cost of rasterising millions of splats scales ~linearly
// with frame rate — so capping at 30 (vs 60) roughly halves GPU load, keeping the scene
// cool and playable on modest/integrated GPUs (older work laptops). Combined with
// render-on-demand (0fps when idle), peak load only occurs during active movement.
const TARGET_FPS_DESKTOP = 30;
const TARGET_FPS_MOBILE = 30;
/** Visible autoplay video/model panels should feel smooth without pinning the splat renderer. */
const ANIMATED_CONTENT_FPS = 18;
/** Loading/debug snapshot rate; the closed, ready HUD is event-driven. */
const HUD_EMIT_INTERVAL_MS = 100;
/** Bootstrap watchdog — surface an error if no splats ever draw (iPad black-screen guard). */
const BOOTSTRAP_TIMEOUT_MS = 45_000;
/** Minimum rendered splat count before bootstrap render mode ends. */
const BOOTSTRAP_SPLAT_THRESHOLD = 10_000;
/** Minimum rendered splats before the shockwave reveal (streamed scenes). */
const ENTRANCE_REVEAL_SPLAT_MIN = 10_000;
/** iOS/WebKit can leave streamed LOD FRAMEREADY pending forever; avoid 90% purgatory. */
const STREAMING_REVEAL_STALL_TIMEOUT_MS = 8_000;
/** Longer settle while the radial reveal is animating. */
const REVEAL_SETTLE_MS = 500;
/** Keep the progress/hint HUD visible long enough to register before the wave owns the scene. */
const REVEAL_PROGRESS_HOLD_S = 1.2;
/** Desktop authored content joins after the splat reveal settles to avoid black/colour flashes. */
const DESKTOP_WORLD_CONTENT_SETTLE_MS = 700;
/** Render authored content invisibly before handoff so panel pipelines compile off-stage. */
const DESKTOP_WORLD_CONTENT_PREWARM_MS = 1_500;
/** Let desktop monolithic SOG WebP decodes/upload settle while the reveal shader is held. */
const DESKTOP_HELD_REVEAL_SETTLE_MS = 700;
/** The shader reaches full colour behind the wave front, so preview dots trail it. */
const PREVIEW_DISSOLVE_TRAIL_RADIUS = 3.25;
/** Hold a final preview tail until the reveal is effectively complete; prevents black gaps. */
const PREVIEW_FINAL_OVERLAP_S = 0.55;
/** Let the world-shaped dot silhouette breathe before releasing the splat wave. */
const SCENE_DOT_HOLD_MS = 900;
/** Welcome message duration before preview dots appear. */
const ENTRANCE_WELCOME_MS = 2_400;
/** Short cache-verification window before a warm revisit falls back to visible dots/progress. */
const WARM_SESSION_VERIFY_MS = 650;
const ENTRANCE_LOGO_URL = '/artwork/logo_hiro01-wht.svg';
const PHOTO_WATERMARK_URL = ENTRANCE_LOGO_URL;
let photoWatermarkImagePromise: Promise<HTMLImageElement | null> | null = null;

function loadPhotoWatermarkImage(): Promise<HTMLImageElement | null> {
  if (!photoWatermarkImagePromise) {
    photoWatermarkImagePromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = PHOTO_WATERMARK_URL;
    });
  }
  return photoWatermarkImagePromise;
}

async function drawPhotoWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): Promise<void> {
  const img = await loadPhotoWatermarkImage();
  if (!img || width <= 0 || height <= 0) return;

  const logoAspect =
    img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 4;
  const maxW = Math.min(width * 0.16, 180);
  const maxH = Math.min(height * 0.065, 54);
  let w = maxW;
  let h = w / logoAspect;
  if (h > maxH) {
    h = maxH;
    w = h * logoAspect;
  }
  const margin = Math.max(16, Math.min(width, height) * 0.026);

  ctx.save();
  ctx.globalAlpha = 0.32;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.22)';
  ctx.shadowBlur = Math.max(4, Math.min(width, height) * 0.006);
  // Bottom-right placement keeps the brand visible without competing with the subject.
  ctx.drawImage(img, width - w - margin, height - h - margin, w, h);
  ctx.restore();
}

function setEntityLayers(entity: pc.Entity, layerIds: number[]): void {
  (entity as pc.Entity & { layers: number[] }).layers = layerIds;
}

function setGsplatLayers(entity: pc.Entity, layerIds: number[]): void {
  setEntityLayers(entity, layerIds);
  const comp = entity.gsplat as (pc.GSplatComponent & { layers?: number[] }) | undefined;
  if (comp?.layers) comp.layers = layerIds;
}
/** Clamp simulation dt after a pause so physics/animation don't jump on resume. */
const MAX_DELTA_TIME = 0.1;

export function resolveSplatBudget(
  preset: PlayCanvasLodPreset,
  worldScene?: LoadedScene,
  splatBudgetMillionsOverride?: number,
  mobileBudget = detectMobile(),
): number {
  const presetBudget = Math.round(preset.splatBudgetMillions * 1_000_000);
  // Hard device cap — never exceeded regardless of preset/world overrides. On mobile
  // this is the primary energy guard: a desktop-sized budget melts phone GPUs.
  const qualityCap =
    typeof splatBudgetMillionsOverride === 'number' && splatBudgetMillionsOverride > 0
      ? Math.round(splatBudgetMillionsOverride * 1_000_000)
      : undefined;
  const deviceCap = mobileBudget
    ? (worldScene?.mobileLodSplatCount ?? MOBILE_LOD_SPLAT_COUNT)
    : DESKTOP_LOD_SPLAT_COUNT;
  const cap = qualityCap ? Math.min(deviceCap, qualityCap) : deviceCap;
  const sceneLod = worldScene?.lodSplatCount;
  if (mobileBudget) {
    // Mobile: clamp BOTH the preset budget and the world LOD count down to the cap.
    const desired = typeof sceneLod === 'number' && sceneLod > 0 ? sceneLod : presetBudget;
    return Math.min(desired, cap);
  }
  if (typeof sceneLod === 'number' && sceneLod > 0) {
    return Math.max(presetBudget, Math.min(sceneLod, cap));
  }
  return presetBudget;
}

/** Scene-wide gsplat tuning aligned with PlayCanvas lod-streaming examples. */
function applyPlayCanvasGsplatSceneDefaults(
  appInstance: pc.AppBase,
  preset: PlayCanvasLodPreset,
  worldScene?: LoadedScene,
  splatBudgetMillionsOverride?: number,
  mobileBudget = detectMobile(),
): void {
  const gs = appInstance.scene.gsplat;
  gs.lodUpdateAngle = preset.lodUpdateAngle;
  gs.lodBehindPenalty = preset.lodBehindPenalty;
  gs.lodUpdateDistance = preset.lodUpdateDistance;
  gs.lodUnderfillLimit = preset.lodUnderfillLimit;
  gs.radialSorting = preset.radialSorting;
  gs.splatBudget = resolveSplatBudget(
    preset,
    worldScene,
    splatBudgetMillionsOverride,
    mobileBudget,
  );
  gs.dataFormat = pc.GSPLATDATA_COMPACT;
  gs.minPixelSize = preset.minPixelSize;
  gs.alphaClipForward = 1 / 255;
  gs.minContribution = preset.minContribution;
  gs.alphaClip = 0.2;
  // AUTO resolves to the hybrid GPU-sort renderer on WebGPU (stable since engine 2.20.0) and
  // CPU sort on WebGL2. The GPU-sort renderer applies GsplatRevealRadial's `gsplatModifyVS`
  // chunk (PR #8822), so the entrance reveal animates natively — no renderer swap needed.
  gs.renderer = pc.GSPLAT_RENDERER_AUTO;
}

/** GSplat entity Euler — PlayCanvas SOG assets use 180° X unless playCanvasBasis is false. */
function resolveGsplatEuler(
  worldScene: LoadedScene | undefined,
  preset: PlayCanvasLodPreset,
): [number, number, number] {
  const [rx, ry, rz] = worldScene?.rotation ?? [0, 0, 0];
  const basisX = worldScene?.playCanvasBasis === false ? 0 : preset.orientationEulerX;
  return [basisX + rx, ry, rz];
}

/** Match PlayCanvas gsplat-octree-instance FOV compensation. */
function computeGsplatFovScale(camera: pc.CameraComponent): number {
  const DEG_TO_RAD = Math.PI / 180;
  const REF_TAN_HALF_FOV = Math.tan(22.5 * DEG_TO_RAD);
  let tanHalfVFov = Math.tan(camera.fov * 0.5 * DEG_TO_RAD);
  if (camera.horizontalFov) {
    tanHalfVFov /= camera.aspectRatio;
  }
  const tanHalfHFov = tanHalfVFov * camera.aspectRatio;
  return Math.min(tanHalfVFov, tanHalfHFov) / REF_TAN_HALF_FOV;
}

/** world.json `lodBaseDistance` is real metres; the engine compares FOV-adjusted distance. */
function applyComponentLodDistances(
  gsplatComp: pc.GSplatComponent,
  camera: pc.CameraComponent,
  lodPreset: PlayCanvasLodPreset,
): void {
  const gs = gsplatLod(gsplatComp);
  gs.lodBaseDistance = lodPreset.lodBaseDistance * computeGsplatFovScale(camera);
  gs.lodMultiplier = lodPreset.lodMultiplier;
}

type GsplatOctreeResource = { octree?: { lodLevels?: number } } | null | undefined;

/** Per-entity LOD fields (PlayCanvas example sets these on the component, not scene.gsplat). */
type GsplatLodComponent = pc.GSplatComponent & {
  lodRangeMin: number;
  lodRangeMax: number;
  lodBaseDistance: number;
  lodMultiplier: number;
};

const gsplatLod = (comp: pc.GSplatComponent): GsplatLodComponent => comp as GsplatLodComponent;

/** Clamp world.json LOD range to the octree depth in lod-meta.json (0 = finest). */
function resolveLodRange(
  lodPreset: PlayCanvasLodPreset,
  resource: GsplatOctreeResource,
): { min: number; max: number } {
  const maxLod =
    resource?.octree?.lodLevels != null && resource.octree.lodLevels > 0
      ? resource.octree.lodLevels - 1
      : lodPreset.lodRangeMax;
  const max = Math.max(0, Math.min(lodPreset.lodRangeMax, maxLod));
  const min = Math.max(0, Math.min(lodPreset.lodRangeMin, max));
  return { min, max };
}

/** LOD index range lives on the GSplat **component**, not app.scene.gsplat (global tuning only). */
function applyComponentLodRange(
  gsplatComp: pc.GSplatComponent,
  lodPreset: PlayCanvasLodPreset,
  resource: GsplatOctreeResource,
  range?: { min: number; max: number },
): void {
  const gs = gsplatLod(gsplatComp);
  const { min, max } = range ?? resolveLodRange(lodPreset, resource);
  gs.lodRangeMin = min;
  gs.lodRangeMax = max;
}

function resolveQualityLodRange(
  quality: MobileQualityRuntimeOptions | undefined,
  resource: GsplatOctreeResource,
): { min: number; max: number } | undefined {
  if (!quality) return undefined;
  const maxLod =
    resource?.octree?.lodLevels != null && resource.octree.lodLevels > 0
      ? resource.octree.lodLevels - 1
      : quality.lodRangeMax;
  const max = Math.max(0, Math.min(quality.lodRangeMax, maxLod));
  const min = Math.max(0, Math.min(quality.lodRangeMin, max));
  return { min, max };
}

export function resolveBootstrapLodRange(
  lodPreset: PlayCanvasLodPreset,
  resource: GsplatOctreeResource,
  quality: Pick<MobileQualityRuntimeOptions, 'lodRangeMin' | 'lodRangeMax'> | undefined,
  mobileBudget: boolean,
): { min: number; max: number } {
  if (!mobileBudget) return { min: 0, max: 0 };
  const resourceMaxLod =
    resource?.octree?.lodLevels != null && resource.octree.lodLevels > 0
      ? resource.octree.lodLevels - 1
      : undefined;
  // Mobile startup must use the smallest available representation. A fixed LOD index is unsafe:
  // deeper mobile-specific trees deliberately add coarse levels beyond the desktop LOD3.
  if (resourceMaxLod != null) return { min: resourceMaxLod, max: resourceMaxLod };
  if (quality) {
    const maxLod =
      resource?.octree?.lodLevels != null && resource.octree.lodLevels > 0
        ? resource.octree.lodLevels - 1
        : quality.lodRangeMax;
    const max = Math.max(0, Math.min(quality.lodRangeMax, maxLod));
    const min = Math.max(0, Math.min(quality.lodRangeMin, max));
    return { min, max };
  }
  return resolveLodRange(lodPreset, resource);
}

function parseBackgroundColor(hex: string | undefined): pc.Color {
  const c = new pc.Color(0, 0, 0);
  if (!hex || !hex.startsWith('#')) return c;
  const n = Number.parseInt(hex.slice(1), 16);
  if (!Number.isFinite(n)) return c;
  c.r = ((n >> 16) & 255) / 255;
  c.g = ((n >> 8) & 255) / 255;
  c.b = (n & 255) / 255;
  return c;
}

function describeGsplatRenderer(
  value: number | undefined,
): PlayCanvasHudSnapshot['gsplatRenderer'] {
  switch (value) {
    case pc.GSPLAT_RENDERER_COMPUTE:
      return 'compute';
    case pc.GSPLAT_RENDERER_RASTER_GPU_SORT:
      return 'raster-gpu';
    case pc.GSPLAT_RENDERER_RASTER_CPU_SORT:
      return 'raster-cpu';
    default:
      return 'auto';
  }
}

function detectMobile(): boolean {
  if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) return true;
  if (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Camera look direction projected onto the XZ plane (PlayCanvas camera faces -Z). */
function horizontalForward(cameraEntity: pc.Entity, out: pc.Vec3): void {
  cameraEntity.getWorldTransform().getZ(out);
  out.mulScalar(-1);
  out.y = 0;
  if (out.lengthSq() < 1e-8) out.set(0, 0, -1);
  else out.normalize();
}

/** Camera right direction projected onto the XZ plane. */
function horizontalRight(cameraEntity: pc.Entity, out: pc.Vec3): void {
  cameraEntity.getWorldTransform().getX(out);
  out.y = 0;
  if (out.lengthSq() < 1e-8) out.set(1, 0, 0);
  else out.normalize();
}

function sessionWarmKey(splatUrl: string): string {
  return `pc-viewer-warm:${splatUrl}`;
}

function isSessionWarm(splatUrl: string, graphicsApi: 'webgpu' | 'webgl2'): boolean {
  try {
    return sessionStorage.getItem(sessionWarmKey(splatUrl)) === `splats:${graphicsApi}`;
  } catch {
    return false;
  }
}

function markSessionWarm(splatUrl: string, graphicsApi: 'webgpu' | 'webgl2'): void {
  try {
    sessionStorage.setItem(sessionWarmKey(splatUrl), `splats:${graphicsApi}`);
  } catch {
    // ignore quota / private mode
  }
}

interface ControlState {
  collision: CollisionSystem;
  worldContent: PlayCanvasWorldContent | null;
  cameraEntity: pc.Entity;
  isTouchMode: boolean;
  touchControls: TouchControls | null;
  isDroneMode: boolean;
  worldReady: () => boolean;
  entranceActive: () => boolean;
  isPlacingMeasure: () => boolean;
  isFlythroughActive: () => boolean;
  isFlythroughCameraLocked: () => boolean;
  pauseFlythroughForInteraction: () => boolean;
  requestRender: () => void;
  syncLookFromEntity?: () => void;
  onPointerLockChange?: (locked: boolean) => void;
  markSpatialInteraction?: () => void;
}

function isMeasurePointerTarget(e: PointerEvent, canvas: HTMLCanvasElement): boolean {
  return e.target === canvas;
}

function attachDesktopControls(
  canvas: HTMLCanvasElement,
  cameraEntity: pc.Entity,
  appInstance: pc.AppBase,
  initialYawDeg: number,
  state: ControlState,
  measureTool: MeasureOverlay,
): () => void {
  const DEG = Math.PI / 180;
  let yaw = initialYawDeg * DEG;
  let pitch = 0;
  const keys = new Set<string>();
  const lookSens = 0.0022;
  const forward = new pc.Vec3();
  const right = new pc.Vec3();
  const up = new pc.Vec3(0, 1, 0);
  const delta = new pc.Vec3();
  const physicsPos = new Vector3();

  const syncRotation = () => {
    cameraEntity.setLocalEulerAngles(pitch / DEG, yaw / DEG, 0);
  };
  syncRotation();
  state.syncLookFromEntity = () => {
    const angles = lookAnglesFromForward(cameraEntity.forward);
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, angles.pitchRad));
    yaw = angles.yawRad;
  };

  /** True while a measure click/drag gesture is in progress (capture phase). */
  let measureGesture = false;
  let entranceLookGesture = false;
  let entranceLookX = 0;
  let entranceLookY = 0;

  const allowLook = () =>
    !state.isFlythroughCameraLocked() &&
    canUpdateCameraLook({
      worldReady: state.worldReady(),
      entranceActive: state.entranceActive(),
      isPlacingMeasure: state.isPlacingMeasure(),
    });

  const applyLookDelta = (dx: number, dy: number) => {
    yaw -= dx * lookSens;
    pitch -= dy * lookSens;
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
    syncRotation();
    state.requestRender();
  };
  const markSpatialInteraction = () => {
    if (state.worldReady()) state.markSpatialInteraction?.();
  };

  const onPtrDownCapture = (e: PointerEvent) => {
    if (e.button !== 0) return;
    if (measureTool.tryStartGizmoDrag(e.clientX, e.clientY)) {
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      measureGesture = true;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (
      !state.worldReady() &&
      state.entranceActive() &&
      !state.isPlacingMeasure() &&
      isMeasurePointerTarget(e, canvas)
    ) {
      entranceLookGesture = true;
      entranceLookX = e.clientX;
      entranceLookY = e.clientY;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!isMeasurePointerTarget(e, canvas)) return;
    if (!measureTool.capturesPointer()) return;
    if (document.pointerLockElement === canvas) document.exitPointerLock();
    measureGesture = true;
    measureTool.handlePointerDown(e.clientX, e.clientY);
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  const onPtrMoveCapture = (e: PointerEvent) => {
    if (entranceLookGesture) {
      applyLookDelta(e.clientX - entranceLookX, e.clientY - entranceLookY);
      entranceLookX = e.clientX;
      entranceLookY = e.clientY;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (measureTool.isDraggingGizmo()) {
      measureTool.handlePointerMove(e.clientX, e.clientY);
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (measureTool.isEditing()) {
      measureTool.handlePointerMove(e.clientX, e.clientY);
    }
    if (!measureGesture) return;
    measureTool.handlePointerMove(e.clientX, e.clientY);
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  const onPtrUpCapture = (e: PointerEvent) => {
    if (measureTool.isDraggingGizmo() && e.button === 0) {
      measureTool.handlePointerUp();
      measureGesture = false;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (entranceLookGesture && e.button === 0) {
      entranceLookGesture = false;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!measureGesture || e.button !== 0) return;
    measureTool.handlePointerUp();
    measureGesture = false;
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  const onClick = (event: MouseEvent) => {
    if (state.pauseFlythroughForInteraction()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!state.worldReady()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (state.isPlacingMeasure()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (measureTool.blocksPointerLock() || measureTool.consumeSuppressPointerLock()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (document.pointerLockElement !== canvas) {
      void canvas.requestPointerLock();
    }
    markSpatialInteraction();
  };
  const onMouseMove = (event: MouseEvent) => {
    if (!allowLook()) return;
    if (document.pointerLockElement !== canvas) return;
    markSpatialInteraction();
    applyLookDelta(event.movementX, event.movementY);
  };
  const onMouseDown = (event: MouseEvent) => {
    if (state.pauseFlythroughForInteraction()) return;
    if (state.isPlacingMeasure()) return;
    if (event.button === 0 && document.pointerLockElement === canvas && state.worldReady()) {
      markSpatialInteraction();
      state.worldContent?.fireInteract(state.cameraEntity);
    }
  };
  const onWheel = () => {
    markSpatialInteraction();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'KeyP') {
      state.isDroneMode = !state.isDroneMode;
      markSpatialInteraction();
      return;
    }
    if (event.code.startsWith('Arrow')) {
      event.preventDefault();
    }
    if (state.isFlythroughCameraLocked()) return;
    if (state.isPlacingMeasure()) return;
    if (event.code === 'KeyF' && state.worldReady()) {
      markSpatialInteraction();
      state.worldContent?.fireInteract(state.cameraEntity);
      return;
    }
    markSpatialInteraction();
    keys.add(event.code);
  };
  const onKeyUp = (event: KeyboardEvent) => {
    if (event.code.startsWith('Arrow')) {
      event.preventDefault();
    }
    keys.delete(event.code);
  };
  const onPointerLockChange = () => {
    if (
      (state.isPlacingMeasure() || measureTool.blocksPointerLock()) &&
      document.pointerLockElement === canvas
    ) {
      document.exitPointerLock();
    }
    state.onPointerLockChange?.(document.pointerLockElement === canvas);
    if (document.pointerLockElement === canvas) markSpatialInteraction();
  };

  const onUpdate = (dt: number) => {
    if (
      state.isFlythroughCameraLocked() ||
      !canMoveCamera({ worldReady: state.worldReady(), isPlacingMeasure: state.isPlacingMeasure() })
    ) {
      return;
    }
    const walkMode = (state.collision.isLoaded && !state.isDroneMode) ?? false;
    if (keys.size === 0) return;

    let speed = walkMode ? COLLISION_MOVE_SPEED : state.isDroneMode ? DRONE_MOVE_SPEED : FLY_SPEED;
    if (keys.has('ShiftLeft') || keys.has('ShiftRight')) {
      speed = walkMode ? COLLISION_SPRINT_SPEED : speed * 2.5;
    } else if (keys.has('ControlLeft') || keys.has('ControlRight')) {
      speed = walkMode ? COLLISION_SLOW_SPEED : speed * 0.35;
    }
    speed *= dt;

    horizontalForward(cameraEntity, forward);
    right.cross(forward, up).normalize();

    delta.set(0, 0, 0);
    if (keys.has('KeyW') || keys.has('ArrowUp')) delta.add(forward);
    if (keys.has('KeyS') || keys.has('ArrowDown')) delta.sub(forward);
    if (keys.has('KeyD') || keys.has('ArrowRight')) delta.add(right);
    if (keys.has('KeyA') || keys.has('ArrowLeft')) delta.sub(right);
    if (!walkMode) {
      if (keys.has('KeyE')) delta.y += 1;
      if (keys.has('KeyQ')) delta.y -= 1;
    }

    if (delta.lengthSq() > 0) {
      delta.normalize().mulScalar(speed);
      const pos = cameraEntity.getLocalPosition();
      physicsPos.set(pos.x + delta.x, pos.y + delta.y, pos.z + delta.z);
      state.collision.applyPhysics(physicsPos, dt, !walkMode, pos);
      cameraEntity.setLocalPosition(physicsPos.x, physicsPos.y, physicsPos.z);
    } else if (walkMode) {
      const pos = cameraEntity.getLocalPosition();
      physicsPos.set(pos.x, pos.y, pos.z);
      state.collision.applyPhysics(physicsPos, dt, false, pos);
      cameraEntity.setLocalPosition(physicsPos.x, physicsPos.y, physicsPos.z);
    }
  };

  document.addEventListener('pointerdown', onPtrDownCapture, { capture: true, passive: false });
  document.addEventListener('pointermove', onPtrMoveCapture, { capture: true, passive: false });
  document.addEventListener('pointerup', onPtrUpCapture, { capture: true, passive: false });
  canvas.addEventListener('click', onClick);
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('wheel', onWheel, { passive: true });
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('pointerlockchange', onPointerLockChange);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  appInstance.on('update', onUpdate);
  onPointerLockChange();

  return () => {
    document.removeEventListener('pointerdown', onPtrDownCapture, { capture: true } as never);
    document.removeEventListener('pointermove', onPtrMoveCapture, { capture: true } as never);
    document.removeEventListener('pointerup', onPtrUpCapture, { capture: true } as never);
    canvas.removeEventListener('click', onClick);
    canvas.removeEventListener('mousedown', onMouseDown);
    canvas.removeEventListener('wheel', onWheel);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('pointerlockchange', onPointerLockChange);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    appInstance.off('update', onUpdate);
    if (document.pointerLockElement === canvas) document.exitPointerLock();
  };
}

function attachTouchControls(
  pickSurface: HTMLElement,
  cameraEntity: pc.Entity,
  appInstance: pc.AppBase,
  initialYawDeg: number,
  state: ControlState,
): () => void {
  const DEG = Math.PI / 180;
  let yaw = initialYawDeg * DEG;
  let pitch = 0;
  const forward = new pc.Vec3();
  const right = new pc.Vec3();
  const up = new pc.Vec3(0, 1, 0);
  const delta = new pc.Vec3();
  const physicsPos = new Vector3();
  const lookDelta = { dx: 0, dy: 0 };

  const syncRotation = () => {
    cameraEntity.setLocalEulerAngles(pitch / DEG, yaw / DEG, 0);
  };
  syncRotation();
  state.syncLookFromEntity = () => {
    const angles = lookAnglesFromForward(cameraEntity.forward);
    pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, angles.pitchRad));
    yaw = angles.yawRad;
  };
  const markSpatialInteraction = () => {
    if (state.worldReady()) state.markSpatialInteraction?.();
  };

  const touch = new TouchControls(pickSurface, {
    onActivity: markSpatialInteraction,
    onTap: (tap) => {
      if (state.pauseFlythroughForInteraction()) return;
      if (state.isPlacingMeasure()) return;
      if (!state.worldReady() || !state.worldContent) return;
      state.worldContent.fireInteractAt(tap.ndcX, tap.ndcY, state.cameraEntity);
    },
  });
  state.touchControls = touch;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'KeyP') {
      state.isDroneMode = !state.isDroneMode;
    }
  };

  const onUpdate = (dt: number) => {
    const { dx, dy } = touch.consumeLookDelta(lookDelta);
    if (
      !state.isFlythroughCameraLocked() &&
      canUpdateCameraLook({
        worldReady: state.worldReady(),
        entranceActive: state.entranceActive(),
        isPlacingMeasure: state.isPlacingMeasure(),
      }) &&
      (dx !== 0 || dy !== 0)
    ) {
      yaw -= dx * TOUCH_LOOK_SENSITIVITY;
      pitch -= dy * TOUCH_LOOK_SENSITIVITY;
      pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
      syncRotation();
      state.requestRender();
    }

    if (
      state.isFlythroughCameraLocked() ||
      !canMoveCamera({ worldReady: state.worldReady(), isPlacingMeasure: state.isPlacingMeasure() })
    ) {
      return;
    }

    const walkMode = state.collision.isLoaded && !state.isDroneMode;
    let speed = walkMode ? COLLISION_MOVE_SPEED : DRONE_MOVE_SPEED;
    speed *= dt;

    if (touch.state.moveActive) {
      horizontalForward(cameraEntity, forward);
      right.cross(forward, up).normalize();

      delta.set(0, 0, 0);
      delta.addScaled(forward, touch.state.moveY * speed);
      delta.addScaled(right, touch.state.moveX * speed);

      const pos = cameraEntity.getLocalPosition();
      physicsPos.set(pos.x + delta.x, pos.y + delta.y, pos.z + delta.z);
      state.collision.applyPhysics(physicsPos, dt, !walkMode, pos);
      cameraEntity.setLocalPosition(physicsPos.x, physicsPos.y, physicsPos.z);
    }
  };

  window.addEventListener('keydown', onKeyDown);
  appInstance.on('update', onUpdate);
  return () => {
    window.removeEventListener('keydown', onKeyDown);
    touch.dispose();
    state.touchControls = null;
    appInstance.off('update', onUpdate);
  };
}

/**
 * PlayCanvas Engine viewer for monolithic or streamed SOG.
 * Point-preview entrance, distance LOD, panels and interactables after reveal.
 */
export async function mountPlayCanvasViewer(
  container: HTMLElement,
  options: MountPlayCanvasViewerOptions,
): Promise<PlayCanvasViewer> {
  const isTouchMode = isTouchPrimaryDevice();
  const isMobileDevice = detectMobile() || options.mobileLayout === true || isTouchMode;
  const basePreset =
    options.preset ?? (isMobileDevice ? PLAYCANVAS_MOBILE_PRESET : PLAYCANVAS_DESKTOP_PRESET);
  const preset = mergePlayCanvasLodPreset(
    basePreset,
    options.worldScene?.lod,
    options.worldScene?.lodMobile,
    isMobileDevice,
  );
  const cameraStart = options.cameraStart ?? [0, 1.6, 3.2];
  const cameraYawDeg = options.cameraYawDeg ?? 0;
  const showTouchEntranceHints = options.mobileLayout === true || isTouchMode;
  const isStreamedSplat = isStreamedSplatUrl(options.splatUrl);
  const isTablet = detectTablet();
  let splatBudget = resolveSplatBudget(
    preset,
    options.worldScene,
    options.initialQuality?.splatBudgetMillions,
    isMobileDevice,
  );

  container.style.position = 'relative';

  const viewport = document.createElement('div');
  viewport.className = 'pc-canvas-viewport';
  viewport.classList.toggle('pc-canvas-viewport--touch', showTouchEntranceHints);
  viewport.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:hidden';
  container.appendChild(viewport);

  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  canvas.style.position = 'relative';
  canvas.style.zIndex = '0';
  viewport.appendChild(canvas);

  const crosshairEl = document.createElement('div');
  crosshairEl.className = 'crosshair';
  crosshairEl.setAttribute('aria-hidden', 'true');
  crosshairEl.style.display = 'none';
  viewport.appendChild(crosshairEl);
  let crosshairClickTimer = 0;
  const pulseCrosshairClick = () => {
    if (isTouchMode || !crosshairEl.classList.contains('crosshair--target')) return;
    window.clearTimeout(crosshairClickTimer);
    crosshairEl.classList.remove('crosshair--press');
    void crosshairEl.offsetWidth;
    crosshairEl.classList.add('crosshair--press');
    crosshairClickTimer = window.setTimeout(() => {
      crosshairEl.classList.remove('crosshair--press');
    }, 280);
  };
  const onCrosshairMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;
    if (document.pointerLockElement !== canvas) return;
    pulseCrosshairClick();
  };
  canvas.addEventListener('mousedown', onCrosshairMouseDown);

  const touchReticleEl = document.createElement('div');
  touchReticleEl.className = 'crosshair touch-reticle';
  touchReticleEl.setAttribute('aria-hidden', 'true');
  touchReticleEl.style.display = showTouchEntranceHints ? '' : 'none';
  viewport.appendChild(touchReticleEl);

  const spatialPrompt = new SpatialControlPrompt(viewport, { touchMode: showTouchEntranceHints });

  const pointerLockNoticeEl = document.createElement('div');
  pointerLockNoticeEl.className = 'gii-pointer-lock-notice';
  pointerLockNoticeEl.setAttribute('role', 'status');
  pointerLockNoticeEl.setAttribute('aria-live', 'polite');
  pointerLockNoticeEl.textContent =
    "Cursor lock activated - press 'Esc' to interact with the menus";
  viewport.appendChild(pointerLockNoticeEl);
  let pointerLockNoticeTimer = 0;
  const showPointerLockNotice = () => {
    if (isTouchMode) return;
    window.clearTimeout(pointerLockNoticeTimer);
    pointerLockNoticeEl.classList.remove(
      'gii-pointer-lock-notice--visible',
      'gii-pointer-lock-notice--faded',
    );
    void pointerLockNoticeEl.offsetWidth;
    pointerLockNoticeEl.classList.add('gii-pointer-lock-notice--visible');
    pointerLockNoticeTimer = window.setTimeout(() => {
      pointerLockNoticeEl.classList.add('gii-pointer-lock-notice--faded');
    }, 4200);
  };
  const hidePointerLockNotice = () => {
    window.clearTimeout(pointerLockNoticeTimer);
    pointerLockNoticeEl.classList.remove(
      'gii-pointer-lock-notice--visible',
      'gii-pointer-lock-notice--faded',
    );
  };

  // Opaque cover removed — entrance uses in-scene preview dots + radial reveal.

  const diagEnabled = isDiagnosticsEnabled();
  const diag = new ViewerDiagnostics(viewport, diagEnabled);
  diag.log(`build ${readBuildId()}`);
  resetEntranceTrace();
  entranceTrace('viewer mount', {
    splat: options.splatUrl.split('/').pop() ?? options.splatUrl,
    monolithic: !isStreamedSplatUrl(options.splatUrl),
  });
  diag.log(`tablet=${isTablet} touch=${isTouchMode} mobile=${isMobileDevice}`);
  diag.log(`viewport ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`);

  const onWindowError = (e: ErrorEvent) => {
    diag.log(`ERROR ${e.message}`);
  };
  const onRejection = (e: PromiseRejectionEvent) => {
    const msg = e.reason instanceof Error ? e.reason.message : String(e.reason);
    diag.log(`REJECT ${msg}`);
  };
  if (diagEnabled) {
    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onRejection);
  }

  let disposed = false;
  let streamingPhase: PlayCanvasHudSnapshot['streamingPhase'] = 'booting';
  let pendingChunkLoads = 0;
  let coarseBootstrapDone = false;
  let streamingStableSinceMs = 0;
  let lastStreamingSplats = -1;
  const streamedProgressState = createStreamedSplatProgressState();
  /** Settled splat count while refining — no pending chunks and count flat for this long. */
  const STREAMING_SETTLE_MS = 3_000;
  let loadState: PlayCanvasHudSnapshot['loadState'] = 'loading';
  let errorMessage: string | undefined;
  let collisionLoaded = false;
  let pointerLocked = false;
  let isDroneMode = false;
  let app: pc.AppBase | null = null;
  let detachControls: (() => void) | null = null;
  let overlay: PlayCanvasWorldContent | null = null;
  let worldEntrance: WorldEntrance | null = null;
  let entranceHud: EntranceHud | null = null;
  let pointDots: PlayCanvasPointDots | null = null;
  let entrancePcvPositions: Float32Array | null = null;
  let entranceLogoFormation: LogoFormation | null = null;
  let welcomeDismissed = false;
  let welcomeDismissAtMs = 0;
  let loadingBarShown = false;
  let warmEntrancePending = false;
  let warmEntranceStartedAtMs = 0;
  let gsplatAssetLoadProgress = 0;
  let gsplatAssetLoadedBytes = 0;
  let gsplatAssetTotalBytes = 0;
  let lastGsplatProgressLogPct = -10;
  let lastGsplatProgressLogMs = 0;
  let worldReadyFired = false;
  let worldReadyLatched = false;
  let miniStats: pc.MiniStats | null = null;
  let gsplatEntity: pc.Entity | null = null;
  let gsplatComp: pc.GSplatComponent | null = null;
  let gsplatOctreeResource: GsplatOctreeResource | null = null;
  const collision = new CollisionSystem();
  const collisionRequired = Boolean(options.collisionUrl && options.sceneTransform);

  let hudAccumulatedSeconds = 0;
  let hudAccumulatedFrames = 0;
  let fps = 0;
  let frameTimeMs = 0;
  let lastGsplatWorkMs = 0;
  const renderHistory: number[] = [];
  let lastHudEmitMs = 0;
  let lastHudWorldInteractiveReady: boolean | undefined;
  let debugPerfEnabled = false;
  let readCameraAuthoringSnapshot: () => CameraAuthoringSnapshot | undefined = () => undefined;
  let appliedDpr = window.devicePixelRatio || 1;
  let activeQuality = options.initialQuality;
  let worldContentShown = false;
  let worldContentPrewarmed = false;
  let worldContentReleaseAtMs = 0;
  let worldChromeFirstUpdate = true;
  let lastWorldChromePointerLocked = pointerLocked;
  let lastWorldChromePlacing = false;
  let graphicsApi: PlayCanvasHudSnapshot['graphicsApi'] = 'webgl2';

  // --- Render throttling (energy saving) ---
  const targetFps = isTouchMode ? TARGET_FPS_MOBILE : TARGET_FPS_DESKTOP;
  const minFrameIntervalMs = 1000 / targetFps - 1; // -1ms slack so we don't drop every other frame
  let lastRenderMs = 0;
  let lastAnimatedContentRenderRequestMs = 0;
  let lastOverlayAnimationCheckMs = Number.NEGATIVE_INFINITY;
  let overlayAnimating = false;
  let pageVisible = !document.hidden;
  let canvasOnScreen = true;

  // --- Render on demand ---
  // A 5–8M splat compute scene rendered every frame pins the GPU and spins laptop fans
  // even when nothing moves. So we only draw while something is actually changing: the
  // camera is moving, the scene is still streaming/settling, the entrance is animating,
  // or on-screen world content (video/anim/hover) is animating. After a change we keep
  // drawing for a short settle window so async LOD streaming + radix sort can catch up.
  const RENDER_SETTLE_MS = 400;
  /** Longer settle while bootstrapping — gsplat streaming only advances during renders. */
  const BOOTSTRAP_SETTLE_MS = 8_000;
  let renderUntilMs = 0;
  let lastSettledSplats = -1;
  /** True once a rendered frame shows a meaningful splat count (ends bootstrap render mode). */
  let hasRenderedSplats = false;
  let lastDiagHeartbeatMs = 0;
  const lastCam = { x: NaN, y: NaN, z: NaN, rx: 0, ry: 0, rz: 0, rw: 0 };
  const requestRender = (settleMs = RENDER_SETTLE_MS) => {
    renderUntilMs = Math.max(renderUntilMs, performance.now() + settleMs);
  };

  const applyRuntimeQuality = (quality: MobileQualityRuntimeOptions): void => {
    activeQuality = quality;
    splatBudget = capSplatBudgetForGraphics(
      resolveSplatBudget(preset, options.worldScene, quality.splatBudgetMillions, isMobileDevice),
      { isWebGPU: device.isWebGPU, isTouchMode, touchBudget: quality.webgl2TouchSplatBudget },
    );
    const gs = appInstance.scene.gsplat;
    gs.splatBudget = splatBudget;
    gs.minPixelSize = quality.minPixelSize;
    gs.minContribution = quality.minContribution;
    applyDprScale(quality.dprScale);
    if (gsplatComp) {
      const qualityRange = resolveQualityLodRange(quality, gsplatOctreeResource);
      applyComponentLodRange(gsplatComp, preset, gsplatOctreeResource, qualityRange);
      const cam = cameraEntity.camera;
      if (cam) applyComponentLodDistances(gsplatComp, cam, preset);
    }
    requestRender(BOOTSTRAP_SETTLE_MS);
    burstLayout();
    emitHud(appInstance.stats.frame.gsplats ?? 0, true);
  };

  // Overlay measure tool is constructed after the camera entity exists.
  let measureTool: MeasureOverlay;
  let photoTool: PhotoOverlay;
  let flythroughController: PlayCanvasFlythroughController | null = null;
  const syncPhotoTourChrome = () => {
    const photoTourActive = photoTool.isActive() && (flythroughController?.isActive() ?? false);
    viewport.classList.toggle('pc-canvas-viewport--photo-tour-active', photoTourActive);
  };
  const needsBootstrapRender = (): boolean =>
    loadState === 'loading' ||
    streamingPhase !== 'full' ||
    pendingChunkLoads > 0 ||
    (worldEntrance?.isActive ?? false) ||
    !hasRenderedSplats;
  // Scratch state for the per-frame frustum that culls off-screen animating content.
  // (The camera component's own frustum/viewMatrix are only refreshed during a render, so
  // we rebuild it here from the camera's live world transform + projection.)
  const cullViewMat = new pc.Mat4();
  const cullViewProjMat = new pc.Mat4();
  const cullFrustum = new pc.Frustum();
  const isAnimatingContentVisible = (): boolean => {
    if (!overlay || !overlay.hasAnimatingContent()) return false;
    const cam = cameraEntity.camera;
    if (!cam) return true;
    cullViewMat.copy(cameraEntity.getWorldTransform()).invert();
    cullViewProjMat.mul2(cam.projectionMatrix, cullViewMat);
    cullFrustum.setFromMat4(cullViewProjMat);
    return overlay.hasVisibleAnimatingContent(cullFrustum);
  };

  const getStreamingFill = (renderedSplats: number) =>
    splatBudget > 0 ? Math.min(1, renderedSplats / splatBudget) : 0;

  const getStreamingDownloadProgress = (fallbackProgress: number): number | undefined => {
    if (!isStreamedSplat) return undefined;
    const progressLodRange =
      (activeQuality ? resolveQualityLodRange(activeQuality, gsplatOctreeResource) : undefined) ??
      resolveLodRange(preset, gsplatOctreeResource);
    observeStreamedSplatProgressAssets(
      streamedProgressState,
      gsplatOctreeResource,
      progressLodRange,
    );
    const observed = summarizeStreamedSplatProgress(streamedProgressState, progressLodRange);
    const expectedTotalBytes = resolveLodByteTarget(
      options.worldScene?.lodByteCounts,
      progressLodRange,
    );
    const estimated = estimateStreamedSplatFileProgress(
      gsplatOctreeResource,
      progressLodRange,
      options.worldScene?.lodByteCounts,
    );
    return resolveStreamedSplatDownloadProgress({
      loadedBytes: observed.loadedBytes,
      reportedTotalBytes: observed.reportedTotalBytes,
      expectedTotalBytes,
      fallbackProgress: estimated.totalBytes > 0 ? estimated.progress : fallbackProgress,
      streamingReady: streamingPhase === 'full',
    }).progress;
  };

  const formatMiB = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;

  const tryCompleteStreaming = (renderedSplats: number, ready: boolean, loadingCount: number) => {
    if (!coarseBootstrapDone || streamingPhase === 'full' || !ready || loadingCount > 0) {
      streamingStableSinceMs = 0;
      return false;
    }
    const fill = getStreamingFill(renderedSplats);
    const now = performance.now();
    if (renderedSplats > 0 && fill >= PLAYCANVAS_STREAMING_FILL) {
      streamingPhase = 'full';
      diag.log(`stream full fill=${(fill * 100).toFixed(0)}% splats=${renderedSplats}`);
      return true;
    }
    if (renderedSplats > 0 && Math.abs(renderedSplats - lastStreamingSplats) > 2000) {
      lastStreamingSplats = renderedSplats;
      streamingStableSinceMs = 0;
      return false;
    }
    if (streamingStableSinceMs === 0) streamingStableSinceMs = now;
    if (now - streamingStableSinceMs >= STREAMING_SETTLE_MS) {
      streamingPhase = 'full';
      diag.log(`stream settled splats=${renderedSplats} fill=${(fill * 100).toFixed(0)}%`);
      return true;
    }
    return false;
  };

  const isWorldReady = () => {
    worldReadyLatched = latchWorldReady(worldReadyLatched, {
      loadStateReady: loadState === 'ready',
      hasRenderedSplats,
      entranceDone: worldEntrance === null || worldEntrance.phase === 'done',
      collisionRequired,
      collisionLoaded,
    });
    return worldReadyLatched;
  };

  const isWorldInteractiveReady = () => worldReadyLatched && (!overlay || worldContentShown);

  const maybePrewarmWorldContent = () => {
    if (
      !showTouchEntranceHints &&
      !worldContentPrewarmed &&
      overlay &&
      !worldContentShown &&
      worldEntrance?.isActive
    ) {
      overlay.prewarmForReveal();
      worldContentPrewarmed = true;
      requestRender(DESKTOP_WORLD_CONTENT_PREWARM_MS);
      logEntrance('world content prewarmed invisibly');
    }
  };

  const syncWorldContentVisibility = (): boolean => {
    const ready = isWorldReady();

    if (!overlay) {
      syncChromeVisibility(ready);
      return false;
    }
    if (!ready) {
      worldContentReleaseAtMs = 0;
    }
    const now = performance.now();
    if (ready && !showTouchEntranceHints && worldContentReleaseAtMs === 0) {
      worldContentReleaseAtMs = now + DESKTOP_WORLD_CONTENT_SETTLE_MS;
      requestRender(DESKTOP_WORLD_CONTENT_SETTLE_MS + 120);
    }
    const desktopContentSettling =
      ready && !showTouchEntranceHints && now < worldContentReleaseAtMs;
    if (desktopContentSettling) {
      requestRender(Math.max(1, worldContentReleaseAtMs - now + 120));
    }
    const shouldShow = ready && !desktopContentSettling;
    syncChromeVisibility(!overlay ? ready : shouldShow);
    if (shouldShow === worldContentShown) return false;
    worldContentShown = shouldShow;
    worldChromeFirstUpdate = true;
    overlay.setWorldContentVisible(shouldShow, showTouchEntranceHints && shouldShow);
    if (shouldShow) {
      collision.resetPhysics();
      fireWorldReady();
      burstLayout();
    }
    return true;
  };

  const syncChromeVisibility = (ready: boolean) => {
    measureTool.setChromeVisible(!isTouchMode && ready);
    photoTool.setChromeVisible(ready);
    flythroughController?.setChromeVisible(ready);
  };

  const emitHud = (renderedSplats = 0, force = false) => {
    const now = performance.now();
    const worldInteractiveReady = isWorldInteractiveReady();
    if (
      !shouldEmitViewerHud({
        force,
        worldInteractiveReady,
        previousWorldInteractiveReady: lastHudWorldInteractiveReady,
        debugPerfEnabled,
        msSinceLastEmit: now - lastHudEmitMs,
        intervalMs: HUD_EMIT_INTERVAL_MS,
      })
    ) {
      return;
    }

    const streamingFill = getStreamingFill(renderedSplats);
    const streamingDownloadProgress = getStreamingDownloadProgress(streamingFill);
    const entrancePhase = worldEntrance?.phase ?? 'done';
    const entranceDotsFill = pointDots
      ? Math.max(pointDots.getSpawnProgress(), pointDots.getMorphProgress())
      : entrancePhase === 'dots'
        ? 0
        : 1;
    const entranceProgress = worldEntrance?.loadProgress ?? 1;
    const displayEntranceProgress = resolveEntranceDisplayProgress({
      entranceProgress,
      streamingFill,
      streamingDownloadProgress,
      streamingReady: !isStreamedSplat || streamingPhase === 'full',
      isStreamedSplat,
      entrancePhase,
    });
    lastHudEmitMs = now;
    lastHudWorldInteractiveReady = worldInteractiveReady;
    const stats = app?.stats;
    const frame = stats?.frame;
    const drawCalls = stats?.drawCalls;
    const vram = stats?.vram as { totalUsed?: number } | undefined;
    options.onHud?.({
      loadState,
      renderedSplats,
      splatBudget,
      lodRangeMin: gsplatComp ? gsplatLod(gsplatComp).lodRangeMin : preset.lodRangeMin,
      lodRangeMax: gsplatComp ? gsplatLod(gsplatComp).lodRangeMax : preset.lodRangeMax,
      streamingPhase,
      streamingFill,
      loadProgress: displayEntranceProgress,
      entrancePhase,
      entranceProgress: displayEntranceProgress,
      entranceDotsFill,
      worldReady: worldInteractiveReady,
      collisionLoaded,
      pointerLocked,
      touchMode: isTouchMode,
      droneMode: isDroneMode,
      fps,
      frameTimeMs,
      renderTimeMs: lastGsplatWorkMs,
      overlayRenderMs: 0,
      devicePixelRatio: appliedDpr,
      gsplatWorkMs: lastGsplatWorkMs,
      renderHistory: renderHistory.slice(),
      graphicsApi,
      gsplatRenderer: describeGsplatRenderer(app?.scene.gsplat.currentRenderer),
      engineFrameMs: frame?.ms ?? frameTimeMs,
      drawCallsTotal: drawCalls?.total ?? 0,
      drawCallsForward: drawCalls?.forward ?? 0,
      gsplatSortMs: frame?.gsplatSort ?? 0,
      gsplatBufferCopyPct: frame?.gsplatBufferCopy ?? 0,
      vramMb: (vram?.totalUsed ?? 0) / (1024 * 1024),
      forwardRenderMs: frame?.forwardTime ?? 0,
      errorMessage,
      diagLines: diag.getLines(),
      diagEnabled,
      canvasSize: { w: canvas.clientWidth, h: canvas.clientHeight },
      bootstrapComplete: hasRenderedSplats,
      cameraAuthoring: readCameraAuthoringSnapshot(),
    });
  };

  const setDebugPerf = (enabled: boolean) => {
    debugPerfEnabled = enabled;
    if (enabled && !miniStats) {
      // getDefaultOptions() is typed as `object` upstream but the constructor wants
      // MiniStatsOptions (not exported); the returned value is the correct shape.
      const options = pc.MiniStats.getDefaultOptions([
        'gsplats',
        'gsplatsCopy',
      ]) as ConstructorParameters<typeof pc.MiniStats>[1];
      miniStats = new pc.MiniStats(appInstance, options);
    } else if (!enabled && miniStats) {
      miniStats.destroy();
      miniStats = null;
    }
    requestRender();
    emitHud(appInstance.stats.frame.gsplats ?? 0, true);
  };

  pc.dracoInitialize(PLAYCANVAS_DRACO);

  // iOS WebKit can fail inside the WebGPU startup path before PlayCanvas reaches its
  // WebGL fallback. Keep touch/iOS startup on WebGL2 unless explicitly opted in.
  const forceWebGpu = new URLSearchParams(window.location.search).get('webgpu') === '1';
  const graphicsDeviceOrder = resolveGraphicsDeviceOrder({
    isTablet,
    isTouchMode,
    isIosWebKit: detectIosWebKit(),
    forceWebGpu,
  });
  const toPlayCanvasDeviceType = (kind: GraphicsDeviceKind) =>
    kind === 'webgpu' ? pc.DEVICETYPE_WEBGPU : pc.DEVICETYPE_WEBGL2;
  const gfxOptions = {
    deviceTypes: graphicsDeviceOrder.map(toPlayCanvasDeviceType),
    antialias: false,
    powerPreference: (isTouchMode ? 'default' : 'high-performance') as
      | 'default'
      | 'high-performance',
  };

  diag.log(`createGraphicsDevice (${graphicsDeviceOrder.join('>')})…`);
  let device: pc.GraphicsDevice;
  try {
    device = await pc.createGraphicsDevice(canvas, gfxOptions);
  } catch (err) {
    diag.log(`GFX FAIL ${err instanceof Error ? err.message : String(err)}`);
    throw err;
  }
  graphicsApi = device.isWebGPU ? 'webgpu' : 'webgl2';
  diag.log(`graphics ${graphicsApi}`);
  device.on('devicelost', () => diag.log('DEVICE LOST'));
  console.info(`[GII] graphics device: ${graphicsApi}`);
  emitHud(0, true);
  if (disposed) {
    device.destroy();
    return {
      resize: () => {},
      setDebugPerf: () => {},
      setOverlayTool: () => {},
      toggleMeasureEdit: () => {},
      setQuality: () => {},
      setMediaAutoplay: () => {},
      getCameraAuthoringSnapshot: () => undefined,
      capturePng: async () => new Blob(),
      dispose: () => {},
    };
  }

  const createOptions = new pc.AppOptions();
  createOptions.graphicsDevice = device;
  createOptions.componentSystems = [
    pc.RenderComponentSystem,
    pc.CameraComponentSystem,
    pc.GSplatComponentSystem,
    pc.AnimComponentSystem,
    // Drives the GsplatRevealRadial entrance effect (a Script on the gsplat entity).
    pc.ScriptComponentSystem,
  ];
  createOptions.resourceHandlers = [pc.TextureHandler, pc.ContainerHandler, pc.GSplatHandler];

  const appInstance = new pc.AppBase(canvas);
  app = appInstance;
  appInstance.init(createOptions);
  diag.log('app.init ok');
  // NONE = honor resizeCanvas(container) dimensions. FILL_WINDOW sizes to window.innerWidth/
  // Height, which breaks picking when the viewer is embedded in a smaller shell.
  appInstance.setCanvasFillMode(pc.FILLMODE_NONE);
  appInstance.setCanvasResolution(pc.RESOLUTION_AUTO);
  // Prevent PlayCanvas from writing inline pixel sizes onto the canvas element.
  // `_allowResize` is a private engine field, so reach it via an indexed cast.
  (appInstance as unknown as Record<string, unknown>)._allowResize = false;
  // Drive rendering manually so we can cap FPS and pause when hidden/offscreen.
  // The update loop still runs every rAF (cheap); only the GPU render is gated.
  appInstance.autoRender = false;
  // Clamp dt so physics/animation don't lurch after the loop resumes from a pause.
  appInstance.maxDeltaTime = MAX_DELTA_TIME;

  const layoutRoot = options.layoutRoot ?? container;

  const dpr = window.devicePixelRatio || 1;
  let baseAppliedDpr = dpr >= 2 ? dpr * 0.5 : dpr;
  // Large iPad canvases at high DPR can exhaust GPU memory — cap to 1× on tablets.
  if (isTablet) baseAppliedDpr = Math.min(baseAppliedDpr, 1);
  const applyDprScale = (scale = 1) => {
    appliedDpr = Math.max(0.5, Math.min(baseAppliedDpr * scale, dpr));
    device.maxPixelRatio = appliedDpr;
  };
  applyDprScale(options.initialQuality?.dprScale);

  diag.log(`splat mode ${isStreamedSplat ? 'streamed-octree' : 'monolithic'}`);

  const clearColor = parseBackgroundColor(options.worldScene?.background);
  applyPlayCanvasGsplatSceneDefaults(
    appInstance,
    preset,
    options.worldScene,
    options.initialQuality?.splatBudgetMillions,
    isMobileDevice,
  );
  splatBudget = capSplatBudgetForGraphics(splatBudget, {
    isWebGPU: device.isWebGPU,
    isTouchMode,
    touchBudget: options.initialQuality?.webgl2TouchSplatBudget,
  });
  appInstance.scene.gsplat.splatBudget = splatBudget;
  if (options.initialQuality) {
    appInstance.scene.gsplat.minPixelSize = options.initialQuality.minPixelSize;
    appInstance.scene.gsplat.minContribution = options.initialQuality.minContribution;
  }
  const getEntranceRevealMode = () =>
    resolveEntranceRevealMode({
      graphicsApi,
      gsplatRenderer: describeGsplatRenderer(appInstance.scene.gsplat.currentRenderer),
    });
  diag.log(
    `gsplat renderer active=${describeGsplatRenderer(appInstance.scene.gsplat.currentRenderer)} ` +
      `reveal=${getEntranceRevealMode()}`,
  );
  if (!device.isWebGPU) {
    console.info(
      `[GII] WebGL2 path — splat budget capped to ${(appInstance.scene.gsplat.splatBudget / 1_000_000).toFixed(1)}M`,
    );
  }

  let lodColorizeDebug = false;
  const onLodDebugKey = (event: KeyboardEvent) => {
    if (event.code !== 'KeyL' || event.repeat) return;
    lodColorizeDebug = !lodColorizeDebug;
    appInstance.scene.gsplat.debug = lodColorizeDebug ? pc.GSPLAT_DEBUG_LOD : pc.GSPLAT_DEBUG_NONE;
    console.info(`[GII] LOD colorize (L): ${lodColorizeDebug ? 'on' : 'off'}`);
    requestRender();
  };
  window.addEventListener('keydown', onLodDebugKey);

  const cameraEntity = new pc.Entity('camera');
  cameraEntity.addComponent('camera', {
    clearColor: new pc.Color(0, 0, 0, 1),
    fov: 65,
    nearClip: 0.1,
    farClip: 5000,
  });
  appInstance.root.addChild(cameraEntity);
  cameraEntity.setLocalPosition(cameraStart[0], cameraStart[1], cameraStart[2]);
  cameraEntity.setLocalEulerAngles(0, cameraYawDeg, 0);
  const camera = cameraEntity.camera!;
  readCameraAuthoringSnapshot = () => {
    const position = cameraEntity.getPosition();
    const forward = cameraEntity.forward;
    const angles = lookAnglesFromForward(forward);
    return {
      position: [position.x, position.y, position.z],
      rotation: [angles.pitchRad * RAD_TO_DEG, angles.yawRad * RAD_TO_DEG, 0],
      forward: [forward.x, forward.y, forward.z],
      fov: camera.fov,
    };
  };
  const defaultPhotoFov = camera.fov;
  // Background clear is applied immediately — the gsplat renders on the world layer the
  // whole time (held invisible by the reveal shader), so no hidden-layer juggling is needed.
  camera.clearColor.copy(clearColor);
  const spatialPromptForward = new pc.Vec3();
  const spatialPromptRight = new pc.Vec3();
  const spatialPromptPhysicsPos = new Vector3();
  const spatialPromptDemoAnchorPosition = new pc.Vec3();
  const spatialPromptDemoAnchorEuler = new pc.Vec3();
  let spatialPromptDemoAnchorPitchRad = 0;
  let spatialPromptDemoAnchorYawRad = 0;
  let spatialPromptDemoAnchorActive = false;

  const logEntrance = (event: string, detail?: Record<string, unknown>) => {
    entranceTrace(event, detail);
    const diagMsg =
      detail && Object.keys(detail).length > 0
        ? `${event} ${Object.entries(detail)
            .map(([k, v]) => `${k}=${v}`)
            .join(' ')}`
        : event;
    diag.log(diagMsg);
  };

  const fireWorldReady = () => {
    if (worldReadyFired) return;
    worldReadyFired = true;
    options.onWorldReady?.();
    window.dispatchEvent(
      new CustomEvent('gii:world-ready', {
        detail: { worldId: options.worldId },
      }),
    );
  };

  measureTool = new MeasureOverlay({
    app: appInstance,
    root: viewport,
    panelRoot: options.layoutRoot ?? container,
    canvas,
    cameraEntity,
    device,
    collision,
    requestRender: () => requestRender(),
    onRequestExit: () => options.onMeasureRequestExit?.(),
    onActiveChange: (active) => {
      if (active) photoTool?.setActive(false);
      if (active && document.pointerLockElement === canvas) {
        document.exitPointerLock();
      }
      requestRender();
    },
  });

  const capturePngImpl = async (opts?: { aspect?: PhotoAspect }) => {
    const aspect = opts?.aspect ?? 'screen';
    measureTool.refreshForCapture();
    requestRender(BOOTSTRAP_SETTLE_MS);
    appInstance.renderNextFrame = true;
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    const src = canvas;
    const sw = src.width;
    const sh = src.height;
    if (sw <= 0 || sh <= 0) throw new Error('Canvas not ready');

    const ratioMap: Record<string, number> = {
      screen: sw / sh,
      '1:1': 1,
      '4:3': 4 / 3,
      '16:9': 16 / 9,
      '9:16': 9 / 16,
    };
    const targetRatio = ratioMap[aspect] ?? sw / sh;

    let cw = sw;
    let ch = sh;
    if (Math.abs(sw / sh - targetRatio) > 1e-3) {
      if (sw / sh > targetRatio) {
        ch = sh;
        cw = Math.round(ch * targetRatio);
      } else {
        cw = sw;
        ch = Math.round(cw / targetRatio);
      }
    }
    const sx = Math.max(0, Math.round((sw - cw) / 2));
    const sy = Math.max(0, Math.round((sh - ch) / 2));

    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');

    try {
      const bmp = await createImageBitmap(src);
      ctx.drawImage(bmp, sx, sy, cw, ch, 0, 0, cw, ch);
      bmp.close();
    } catch {
      ctx.drawImage(src, sx, sy, cw, ch, 0, 0, cw, ch);
    }

    if (measureTool.shouldCompositeForCapture()) {
      measureTool.compositeCaptureLabels(ctx, { sx: 0, sy: 0, cw, ch });
    }
    await drawPhotoWatermark(ctx, cw, ch);

    return await new Promise<Blob>((resolve, reject) => {
      out.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encode failed'))), 'image/png');
    });
  };

  photoTool = new PhotoOverlay({
    root: viewport,
    panelRoot: options.layoutRoot ?? container,
    canvas,
    captureFn: (aspect) => capturePngImpl({ aspect }),
    saveBlob: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const id = options.worldId ?? 'scene';
      a.download = `hiro_${id}_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    setLens: (lens: PhotoLens) => {
      const cam = cameraEntity.camera;
      if (!cam) return;
      cam.fov = photoLensToFov(defaultPhotoFov, lens);
      requestRender(BOOTSTRAP_SETTLE_MS);
    },
    requestRender: () => requestRender(),
    onActiveChange: (active) => {
      if (active) measureTool.setEditMode(false);
      flythroughController?.setPhotoModeActive(active);
      syncPhotoTourChrome();
      requestRender();
    },
  });
  syncChromeVisibility(false);

  // Depth prepass: splats do not write depth; the gsplat rasterizer depth-tests
  // against opaque scene geometry (panels, video screens) via the linear depth map.
  const cameraFrame = new pc.CameraFrame(appInstance, cameraEntity.camera!);
  cameraFrame.rendering.sceneDepthMap = true;
  cameraFrame.rendering.toneMapping = pc.TONEMAP_LINEAR;
  cameraFrame.enabled = true;
  cameraFrame.update();

  /**
   * Keep canvas CSS at 100% and sync backing-store size from clientWidth/Height.
   * Never call appInstance.resizeCanvas — it sets inline pixel styles that stick
   * at the pre-fullscreen size and leave a blue bar + broken picking.
   */
  const canvasLayoutState = createCanvasLayoutSyncState();
  let canvasLayoutDirty = true;
  const applyCanvasLayout = (force = false): boolean => {
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (!shouldSyncCanvasLayout(canvasLayoutState, { width: cw, height: ch }, force)) {
      canvasLayoutDirty = cw < 1 || ch < 1;
      return cw >= 1 && ch >= 1;
    }

    device.resizeCanvas(cw, ch);
    device.updateClientRect();

    const cam = cameraEntity.camera;
    if (cam) {
      cam.aspectRatio = cw / ch;
      if (gsplatComp) {
        applyComponentLodDistances(gsplatComp, cam, preset);
      }
    }

    const canvasRect = canvas.getBoundingClientRect();
    const vpRect = viewport.getBoundingClientRect();
    const cx = canvasRect.left - vpRect.left + canvasRect.width / 2;
    const cy = canvasRect.top - vpRect.top + canvasRect.height / 2;
    for (const el of [crosshairEl, touchReticleEl]) {
      el.style.left = `${cx}px`;
      el.style.top = `${cy}px`;
      el.style.transform = 'translate(-50%, -50%)';
    }

    canvasLayoutDirty = false;
    return true;
  };

  /** Re-measure across layout frames — fullscreen CSS can lag one frame behind the API. */
  const burstLayout = () => {
    canvasLayoutDirty = true;
    requestRender();
    applyCanvasLayout(true);
    photoTool.layoutFrame();
    const cw = Math.max(1, canvas.clientWidth);
    const ch = Math.max(1, canvas.clientHeight);
    void cw;
    void ch;
    requestAnimationFrame(() => {
      applyCanvasLayout(true);
      photoTool.layoutFrame();
      requestAnimationFrame(() => {
        applyCanvasLayout(true);
        photoTool.layoutFrame();
      });
    });
  };

  const ro = new ResizeObserver(() => burstLayout());
  ro.observe(container);
  if (layoutRoot !== container) {
    ro.observe(layoutRoot);
  }
  const onFullscreenChange = () => burstLayout();
  document.addEventListener('fullscreenchange', onFullscreenChange);
  burstLayout();

  // Pause GPU rendering when the tab is hidden — no point drawing splats nobody sees.
  const onVisibilityChange = () => {
    pageVisible = !document.hidden;
    lastRenderMs = 0; // force an immediate redraw on resume
    requestRender();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Pause rendering when a top-level canvas is scrolled off-screen. Cross-origin
  // fullscreen embeds can report false negatives and freeze immediately after entrance.
  const skipIntersectionGate = shouldSkipCanvasIntersectionGate({
    touchMode: isTouchMode,
    tablet: isTablet,
    embedded: window.self !== window.top,
  });
  const io = new IntersectionObserver(
    (entries) => {
      if (skipIntersectionGate) return;
      for (const entry of entries) {
        canvasOnScreen = entry.isIntersecting;
      }
      lastRenderMs = 0;
      requestRender();
    },
    { threshold: 0 },
  );
  io.observe(canvas);

  const warmSession = isSessionWarm(options.splatUrl, graphicsApi);
  worldEntrance = new WorldEntrance(appInstance, {
    endRadius: options.worldScene?.revealRadius ?? 45,
    speed: warmSession ? 14 : undefined,
    delay: warmSession ? 0.35 : undefined,
    bandWidth: warmSession ? 1.4 : undefined,
  });

  const ensureEntranceHud = () => {
    if (entranceHud) return entranceHud;
    entranceHud = new EntranceHud(viewport);
    entranceHud.setWelcomeTitle('Loading…');
    return entranceHud;
  };

  const showColdWelcome = () => {
    const hud = ensureEntranceHud();
    hud.showWelcome();
    welcomeDismissAtMs = performance.now() + ENTRANCE_WELCOME_MS;
    logEntrance('welcome shown', { durationMs: ENTRANCE_WELCOME_MS });
  };

  // Warm revisits are candidates for a fast reveal, but only if the splat becomes usable
  // almost immediately. If the browser has evicted/bypassed cache, fall back to dots.
  if (warmSession) {
    warmEntrancePending = true;
    warmEntranceStartedAtMs = performance.now();
    worldEntrance.beginLoading(0, splatBudget);
    camera.clearColor.copy(clearColor);
    requestRender(BOOTSTRAP_SETTLE_MS);
    logEntrance('warm session candidate — verifying cache');
  } else {
    // Reveal runs on the device's normal renderer — the hybrid GPU-sort renderer (WebGPU)
    // applies GsplatRevealRadial's shader chunk natively, so no renderer swap is needed.
    showColdWelcome();
  }
  burstLayout();

  const controlState: ControlState = {
    collision,
    worldContent: null,
    cameraEntity,
    isTouchMode,
    touchControls: null,
    get isDroneMode() {
      return isDroneMode;
    },
    set isDroneMode(v: boolean) {
      isDroneMode = v;
      requestRender();
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
    worldReady: () => isWorldInteractiveReady(),
    entranceActive: () => worldEntrance?.isActive ?? false,
    isPlacingMeasure: () => measureTool.isPlacing(),
    isFlythroughActive: () => flythroughController?.isActive() ?? false,
    isFlythroughCameraLocked: () => flythroughController?.isCameraLocked() ?? false,
    pauseFlythroughForInteraction: () => flythroughController?.pauseForWorldInteraction() ?? false,
    requestRender: () => requestRender(),
    markSpatialInteraction: () => {
      spatialPrompt.markInteraction();
      requestRender();
    },
    onPointerLockChange: (locked) => {
      pointerLocked = locked;
      flythroughController?.setPointerLocked(locked);
      crosshairEl.style.display = locked ? '' : 'none';
      if (locked) {
        showPointerLockNotice();
      } else {
        hidePointerLockNotice();
      }
      requestRender();
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
  };

  const captureSpatialPromptDemoAnchor = () => {
    spatialPromptDemoAnchorPosition.copy(cameraEntity.getLocalPosition());
    const angles = lookAnglesFromForward(cameraEntity.forward);
    spatialPromptDemoAnchorPitchRad = Math.max(
      -PITCH_LIMIT,
      Math.min(PITCH_LIMIT, angles.pitchRad),
    );
    spatialPromptDemoAnchorYawRad = angles.yawRad;
    spatialPromptDemoAnchorEuler.set(
      spatialPromptDemoAnchorPitchRad * RAD_TO_DEG,
      spatialPromptDemoAnchorYawRad * RAD_TO_DEG,
      0,
    );
    spatialPromptDemoAnchorActive = true;
  };

  const setCameraToSpatialPromptDemoAnchor = (
    position: Readonly<{ x: number; y: number; z: number }> = spatialPromptDemoAnchorPosition,
    euler: Readonly<{ x: number; y: number; z: number }> = spatialPromptDemoAnchorEuler,
  ) => {
    if (!spatialPromptDemoAnchorActive) captureSpatialPromptDemoAnchor();
    cameraEntity.setLocalPosition(position.x, position.y, position.z);
    cameraEntity.setLocalEulerAngles(euler.x, euler.y, euler.z);
    controlState.syncLookFromEntity?.();
    requestRender();
  };

  const applySpatialControlDemoAction = (
    sample: SpatialControlDemoActionSample,
    dtSeconds: number,
    demoState: SpatialControlDemoState,
  ) => {
    if (!isWorldInteractiveReady() || demoState !== 'running') return;
    if (!spatialPromptDemoAnchorActive) captureSpatialPromptDemoAnchor();
    if (dtSeconds <= 0) {
      setCameraToSpatialPromptDemoAnchor();
      return;
    }

    const travel = sample.travel;
    const moveMeters = showTouchEntranceHints
      ? SPATIAL_CONTROL_DEMO_TOUCH_MOVE_OFFSET_M
      : SPATIAL_CONTROL_DEMO_MOVE_OFFSET_M;
    const lookDegrees = showTouchEntranceHints
      ? SPATIAL_CONTROL_DEMO_TOUCH_LOOK_OFFSET_DEG
      : SPATIAL_CONTROL_DEMO_LOOK_OFFSET_DEG;
    const pitchLimitDegrees = (PITCH_LIMIT * 180) / Math.PI;

    const moveFromAnchor = (moveForwardUnits: number, moveRightUnits: number) => {
      cameraEntity.setLocalPosition(
        spatialPromptDemoAnchorPosition.x,
        spatialPromptDemoAnchorPosition.y,
        spatialPromptDemoAnchorPosition.z,
      );
      cameraEntity.setLocalEulerAngles(
        spatialPromptDemoAnchorEuler.x,
        spatialPromptDemoAnchorEuler.y,
        spatialPromptDemoAnchorEuler.z,
      );
      horizontalForward(cameraEntity, spatialPromptForward);
      horizontalRight(cameraEntity, spatialPromptRight);
      spatialPromptPhysicsPos.set(
        spatialPromptDemoAnchorPosition.x +
          spatialPromptForward.x * moveMeters * moveForwardUnits +
          spatialPromptRight.x * moveMeters * moveRightUnits,
        spatialPromptDemoAnchorPosition.y,
        spatialPromptDemoAnchorPosition.z +
          spatialPromptForward.z * moveMeters * moveForwardUnits +
          spatialPromptRight.z * moveMeters * moveRightUnits,
      );
      setCameraToSpatialPromptDemoAnchor({
        x: spatialPromptPhysicsPos.x,
        y: spatialPromptPhysicsPos.y,
        z: spatialPromptPhysicsPos.z,
      });
    };

    const mix = (from: number, to: number) => from + (to - from) * travel;
    const lookFromAnchor = (yawUnits: number, pitchUnits: number) => {
      setCameraToSpatialPromptDemoAnchor(spatialPromptDemoAnchorPosition, {
        x: Math.max(
          -pitchLimitDegrees,
          Math.min(
            pitchLimitDegrees,
            spatialPromptDemoAnchorPitchRad * RAD_TO_DEG + pitchUnits * lookDegrees,
          ),
        ),
        y: spatialPromptDemoAnchorYawRad * RAD_TO_DEG + yawUnits * lookDegrees,
        z: 0,
      });
    };

    let moveForwardUnits = 0;
    let moveRightUnits = 0;
    switch (sample.action) {
      case 'forward':
        moveForwardUnits = travel;
        moveFromAnchor(moveForwardUnits, moveRightUnits);
        break;
      case 'back':
        if (showTouchEntranceHints) {
          moveFromAnchor(1 - travel, 0);
        } else {
          moveForwardUnits = 1 - travel;
          moveFromAnchor(moveForwardUnits, moveRightUnits);
        }
        break;
      case 'left':
        if (showTouchEntranceHints) {
          moveFromAnchor(0, -travel);
        } else {
          moveRightUnits = -travel;
          moveFromAnchor(moveForwardUnits, moveRightUnits);
        }
        break;
      case 'right':
        if (showTouchEntranceHints) {
          moveFromAnchor(0, -1 + travel);
        } else {
          moveRightUnits = -1 + travel;
          moveFromAnchor(moveForwardUnits, moveRightUnits);
        }
        break;
      case 'lookLeft': {
        if (showTouchEntranceHints) {
          lookFromAnchor(mix(0, -1), mix(0, 0.22));
        } else {
          lookFromAnchor(-travel, 0);
        }
        break;
      }
      case 'lookRight': {
        if (showTouchEntranceHints) {
          lookFromAnchor(mix(-1, 1), mix(0.22, -0.18));
        } else {
          lookFromAnchor(mix(-1, 1), 0);
        }
        break;
      }
      case 'lookUp': {
        if (showTouchEntranceHints) {
          lookFromAnchor(mix(1, 0.35), mix(-0.18, 1));
        } else {
          lookFromAnchor(mix(1, 0), travel);
        }
        break;
      }
      case 'lookDown': {
        if (showTouchEntranceHints) {
          lookFromAnchor(mix(0.35, -0.35), mix(1, -1));
        } else {
          lookFromAnchor(0, mix(1, -1));
        }
        break;
      }
      default:
        setCameraToSpatialPromptDemoAnchor();
        break;
    }
  };

  flythroughController = new PlayCanvasFlythroughController({
    root: viewport,
    cameraEntity,
    flythroughs: options.worldScene?.flythroughs ?? [],
    getWorldContent: () => overlay,
    requestRender: (settleMs) => requestRender(settleMs),
    syncLookFromEntity: () => controlState.syncLookFromEntity?.(),
    onActiveChange: (active) => {
      if (active) {
        measureTool.setEditMode(false);
        photoTool.setActive(false);
        if (document.pointerLockElement === canvas) document.exitPointerLock();
      }
      syncPhotoTourChrome();
      requestRender(BOOTSTRAP_SETTLE_MS);
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
  });

  if (isTouchMode) {
    detachControls = attachTouchControls(
      canvas,
      cameraEntity,
      appInstance,
      cameraYawDeg,
      controlState,
    );
  } else {
    detachControls = attachDesktopControls(
      canvas,
      cameraEntity,
      appInstance,
      cameraYawDeg,
      controlState,
      measureTool,
    );
  }

  if (options.collisionUrl && options.sceneTransform) {
    void collision.load(options.collisionUrl, options.sceneTransform).then((ok) => {
      collisionLoaded = ok;
      diag.log(ok ? 'collision loaded' : 'collision load failed — interaction remains locked');
      collision.resetPhysics();
      syncWorldContentVisibility();
      requestRender();
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    });
  }

  if (options.worldScene) {
    overlay = new PlayCanvasWorldContent(
      appInstance,
      device,
      options.worldScene.playCanvasBasis ?? true,
      applyCanvasLayout,
      options.initialMediaAutoplay ?? true,
    );
    controlState.worldContent = overlay;
    void overlay
      .load(options.worldScene)
      .then(() => {
        maybePrewarmWorldContent();
        syncWorldContentVisibility();
        requestRender();
        burstLayout();
      })
      .catch((err) => {
        console.warn('[GII] World content load failed:', err);
      });
  }

  let customAsset: pc.Asset | null = null;
  let gsplatLoadStarted = false;
  let gsplatAssetReady = false;
  let pointMorphDoneAtMs = 0;
  let revealShaderStableSinceMs = 0;
  let revealStreamWaitStartedMs = 0;
  let lastFrameReadySig = '';
  let stuckPendingSinceMs = 0;
  let lastStuckPending = -1;

  const splatReadyForMorph = () => gsplatAssetReady && gsplatEntity !== null;
  const previewTargetReadyForMorph = () => Boolean(options.worldScene) || splatReadyForMorph();

  const authoredSplatTransform = new pc.Mat4();
  if (options.worldScene) {
    const [ex, ey, ez] = resolveGsplatEuler(options.worldScene, preset);
    authoredSplatTransform.setTRS(
      new pc.Vec3(
        options.worldScene.position[0],
        options.worldScene.position[1],
        options.worldScene.position[2],
      ),
      new pc.Quat().setFromEulerAngles(ex, ey, ez),
      new pc.Vec3(
        options.worldScene.scale[0],
        options.worldScene.scale[1],
        options.worldScene.scale[2],
      ),
    );
  }

  const beginGsplatLoad = () => {
    if (gsplatLoadStarted || loadState === 'error') return;
    gsplatLoadStarted = true;
    requestRender(BOOTSTRAP_SETTLE_MS);
    logEntrance('gsplat load started', {
      file: options.splatUrl.split('/').pop() ?? options.splatUrl,
      phase: worldEntrance?.phase ?? 'none',
    });
    void loadGsplat(options.splatUrl)
      .then((entity) => {
        gsplatEntity = entity;
        worldEntrance?.setAssetReady(true);
        tryStartPointDots();
        syncWorldContentVisibility();
      })
      .catch((err) => {
        loadState = 'error';
        errorMessage = err instanceof Error ? err.message : 'Splat load failed';
        diag.log(`gsplat FAIL ${errorMessage}`);
        console.error('[GII] Gsplat load failed:', err);
        entranceHud?.hideWelcome();
        entranceHud?.hideProgress();
        emitHud(0, true);
      });
  };

  const pcvToWorld = (local: pc.Vec3, out: pc.Vec3) => {
    if (gsplatEntity) gsplatEntity.getWorldTransform().transformPoint(local, out);
    else if (options.worldScene) authoredSplatTransform.transformPoint(local, out);
    else out.copy(local);
  };

  /** Reveal focus in world space — shared origin for the gsplat reveal wave and dot dissolve. */
  const resolveRevealFocusWorld = (): pc.Vec3 => {
    const worldFocus = new pc.Vec3();
    const label = options.worldScene?.labels?.[0];
    if (label) {
      worldFocus.set(label.position[0], label.position[1], label.position[2]);
    } else {
      const [cx, cy, cz] = options.cameraStart ?? [0, 1.5, 0];
      const yawRad = ((options.cameraYawDeg ?? 0) * Math.PI) / 180;
      worldFocus.set(cx + Math.sin(yawRad) * 4, cy, cz + Math.cos(yawRad) * 4);
    }
    return worldFocus;
  };

  const resolveRevealCenterForRenderer = (): { center: pc.Vec3; space: RevealCenterSpace } => {
    const worldFocus = resolveRevealFocusWorld();
    const space = revealCenterSpaceForRenderer(
      appInstance.scene.gsplat.currentRenderer,
      pc.GSPLAT_RENDERER_RASTER_GPU_SORT,
    );
    return { center: worldFocus, space };
  };

  const startPointDots = () => {
    if (!entrancePcvPositions || pointDots || !worldEntrance) return;
    applyCanvasLayout();
    const cw = Math.max(1, canvas.clientWidth || window.innerWidth || 800);
    const ch = Math.max(1, canvas.clientHeight || window.innerHeight || 600);
    const sceneRoot = appInstance.root;
    pointDots = new PlayCanvasPointDots(
      device,
      entrancePcvPositions,
      ENTRANCE_DOT_COUNT,
      cameraEntity,
      sceneRoot,
      cw,
      ch,
      entranceLogoFormation,
    );
    // Share the reveal wave origin so preview points dissolve radially in step with the wave.
    pointDots.setRevealCenterWorld(resolveRevealFocusWorld());
    pointDots.beginGridSpawn(performance.now());
    worldEntrance.beginDots();
    showLoadingBar(appInstance.stats.frame.gsplats ?? 0);
    camera.clearColor.copy(clearColor);
    logEntrance('preview dots spawn started', {
      canvasW: cw,
      canvasH: ch,
      formation: entranceLogoFormation ? 'logo' : 'grid',
      gsplatEnabled: gsplatEntity?.enabled ?? false,
    });
    requestRender(BOOTSTRAP_SETTLE_MS);
  };

  const tryStartPointDots = () => {
    if (
      !canStartPreviewDots({
        welcomeDismissed,
        hasPcvPreview: Boolean(entrancePcvPositions),
        hasPointDots: Boolean(pointDots),
        hasWorldEntrance: Boolean(worldEntrance),
        entrancePhase: worldEntrance?.phase ?? 'done',
        hasGsplatEntity: Boolean(gsplatEntity),
        requireSplatEntity: false,
      })
    ) {
      return;
    }
    startPointDots();
  };

  const dismissWelcome = () => {
    if (welcomeDismissed) return;
    welcomeDismissed = true;
    entranceHud?.hideWelcome();
    logEntrance('welcome dismissed', {
      hasPcv: Boolean(entrancePcvPositions),
      gsplatAssetReady,
      gsplatEntity: Boolean(gsplatEntity),
    });
    if (entrancePcvPositions) {
      tryStartPointDots();
    } else if (worldEntrance && worldEntrance.phase === 'welcome') {
      const renderedSplats = appInstance.stats.frame.gsplats ?? 0;
      const targetSplats = Math.max(splatBudget, renderedSplats + 1);
      worldEntrance.beginLoading(renderedSplats, targetSplats, gsplatAssetLoadProgress);
      if (!(gsplatAssetReady && renderedSplats >= splatBudget * 0.9)) {
        entranceHud?.showProgress();
      }
      loadingBarShown = true;
      logEntrance('loading without preview dots');
    }
  };

  const showLoadingBar = (renderedSplats: number) => {
    if (loadingBarShown || !worldEntrance) return;
    loadingBarShown = true;
    const targetSplats = Math.max(splatBudget, renderedSplats + 1);
    worldEntrance.beginLoading(renderedSplats, targetSplats, gsplatAssetLoadProgress);
    if (gsplatAssetReady && renderedSplats >= splatBudget * 0.9) {
      logEntrance('loading bar skipped — splat already ready', {
        baselineSplats: renderedSplats,
        targetSplats,
        phase: worldEntrance.phase,
      });
      return;
    }
    entranceHud?.showProgress();
    logEntrance('loading bar visible', {
      baselineSplats: renderedSplats,
      targetSplats,
      phase: worldEntrance.phase,
    });
  };

  const beginWarmEntranceFallback = (renderedSplats: number) => {
    if (!warmEntrancePending || !worldEntrance) return;
    warmEntrancePending = false;
    welcomeDismissed = true;
    ensureEntranceHud();
    entranceHud?.hideWelcome();
    showLoadingBar(renderedSplats);
    tryStartPointDots();
    requestRender(BOOTSTRAP_SETTLE_MS);
    logEntrance('warm cache miss — showing dots/loading', {
      renderedSplats,
      assetReady: gsplatAssetReady,
      elapsedMs: Math.round(performance.now() - warmEntranceStartedAtMs),
    });
  };

  const ensureCoarseLodOpen = () => {
    if (coarseBootstrapDone || !gsplatOctreeResource || !gsplatComp) return;
    applyFullLodPreset(gsplatOctreeResource);
    logEntrance('LOD range opened');
  };

  const runReveal = () => {
    if (
      !gsplatEntity ||
      !worldEntrance ||
      worldEntrance.phase === 'reveal' ||
      worldEntrance.phase === 'done'
    ) {
      return;
    }
    if (worldEntrance.phase !== 'loading' && worldEntrance.phase !== 'dots') return;
    // Wait for the preview dots to finish morphing into the world silhouette so the dot
    // cloud and the wave start from the same shape — a continuous hand-off, no jump.
    const isPointMorphDone = !pointDots || pointDots.isMorphDone();
    if (!isPointMorphDone) {
      entranceTraceThrottled('reveal-wait-morph', 800, 'reveal waiting for dot morph');
      return;
    }
    if (
      pointDots &&
      pointMorphDoneAtMs > 0 &&
      performance.now() - pointMorphDoneAtMs < SCENE_DOT_HOLD_MS
    ) {
      entranceTraceThrottled('reveal-wait-scene-hold', 800, 'reveal waiting for scene-dot hold');
      return;
    }
    if (!gsplatAssetReady) {
      entranceTraceThrottled('reveal-wait-asset', 800, 'reveal waiting for splat asset');
      return;
    }
    // The reveal shader must be live on the gsplat material before we release the hold,
    // otherwise the first un-held frame could show raw splats. It is applied as soon as
    // the held script runs once with a loaded gsplat.
    if (!worldEntrance.isRevealShaderApplied()) {
      revealShaderStableSinceMs = 0;
      entranceTraceThrottled('reveal-wait-shader', 400, 'reveal waiting for shader material');
      return;
    }

    const renderedSplats = appInstance.stats.frame.gsplats ?? 0;
    const revealStabilityRequired = !showTouchEntranceHints && !isStreamedSplat;
    if (revealStabilityRequired) {
      if (renderedSplats < ENTRANCE_REVEAL_SPLAT_MIN) {
        revealShaderStableSinceMs = 0;
        entranceTraceThrottled('reveal-wait-held-splats', 400, 'reveal waiting for held splats', {
          renderedSplats,
        });
        requestRender(DESKTOP_HELD_REVEAL_SETTLE_MS + 120);
        return;
      }
      if (revealShaderStableSinceMs === 0) {
        revealShaderStableSinceMs = performance.now();
        requestRender(DESKTOP_HELD_REVEAL_SETTLE_MS + 120);
      }
      if (performance.now() - revealShaderStableSinceMs < DESKTOP_HELD_REVEAL_SETTLE_MS) {
        entranceTraceThrottled(
          'reveal-wait-held-stability',
          400,
          'reveal waiting for held shader settle',
          {
            renderedSplats,
            settleMs: Math.round(performance.now() - revealShaderStableSinceMs),
          },
        );
        requestRender(DESKTOP_HELD_REVEAL_SETTLE_MS + 120);
        return;
      }
    }
    const streamingReady = !isStreamedSplat || streamingPhase === 'full';
    if (streamingReady) {
      revealStreamWaitStartedMs = 0;
    } else if (revealStreamWaitStartedMs === 0) {
      revealStreamWaitStartedMs = performance.now();
    }
    const streamWaitMs = streamingReady ? 0 : performance.now() - revealStreamWaitStartedMs;
    const allowStreamingTimeout = shouldAllowStreamingRevealTimeout({
      isStreamedSplat,
      showTouchEntranceHints,
    });
    const usingStreamTimeout =
      allowStreamingTimeout &&
      !streamingReady &&
      streamWaitMs >= STREAMING_REVEAL_STALL_TIMEOUT_MS &&
      renderedSplats >= ENTRANCE_REVEAL_SPLAT_MIN;
    if (
      !canReleaseReveal({
        renderedSplats,
        minRenderedSplats: ENTRANCE_REVEAL_SPLAT_MIN,
        hasGsplatEntity: Boolean(gsplatEntity),
        hasAssetReady: gsplatAssetReady,
        hasRevealShader: worldEntrance.isRevealShaderApplied(),
        isPointMorphDone,
        streamingReady,
        allowStreamingTimeout,
        streamWaitMs,
        streamTimeoutMs: STREAMING_REVEAL_STALL_TIMEOUT_MS,
      })
    ) {
      entranceTraceThrottled('reveal-wait-stream', 500, 'reveal waiting for settled stream', {
        renderedSplats,
        streamed: isStreamedSplat,
        streamingPhase,
        pendingChunkLoads,
        streamWaitMs: Math.round(streamWaitMs),
      });
      requestRender(BOOTSTRAP_SETTLE_MS);
      return;
    }

    worldEntrance.startReveal();
    requestRender(REVEAL_SETTLE_MS);
    logEntrance('reveal released', {
      mode: getEntranceRevealMode(),
      renderedSplats,
      streamingPhase,
      streamTimeout: usingStreamTimeout,
      effectTimeS: worldEntrance.revealEffectTimeS.toFixed(3),
      previewDotsKept: Boolean(pointDots),
    });
  };

  const tickEntranceSequence = (dt: number, nowMs: number, renderedSplats: number) => {
    if (!worldEntrance?.isActive) return;

    if (warmEntrancePending) {
      const hasUsableWarmSplat = gsplatAssetReady && renderedSplats >= ENTRANCE_REVEAL_SPLAT_MIN;
      if (hasUsableWarmSplat) {
        warmEntrancePending = false;
        logEntrance('warm session cache verified — fast reveal');
      } else if (
        shouldFallbackWarmEntrance({
          sessionWarm: warmSession,
          hasAssetReady: gsplatAssetReady,
          hasRenderedSplats: renderedSplats >= ENTRANCE_REVEAL_SPLAT_MIN,
          elapsedMs: nowMs - warmEntranceStartedAtMs,
          verifyWindowMs: WARM_SESSION_VERIFY_MS,
        })
      ) {
        beginWarmEntranceFallback(renderedSplats);
      }
    }

    if (worldEntrance.phase === 'welcome' && nowMs >= welcomeDismissAtMs) {
      dismissWelcome();
    }

    tryStartPointDots();

    // Keep the reveal frozen at t=0 while held — splats stay invisible behind the dots
    // until we explicitly release them, so the splat layer never flashes during loading.
    worldEntrance.tickHold();

    if (pointDots) {
      pointDots.tick(nowMs, dt);
      if (
        pointDots.isGridComplete() &&
        pointDots.isWaitingForSplat() &&
        previewTargetReadyForMorph() &&
        pointDots.tryBeginMorph(true, pcvToWorld)
      ) {
        requestRender(BOOTSTRAP_SETTLE_MS);
        logEntrance('preview dots morph started', {
          renderedSplats,
          gsplatAssetReady,
          target: gsplatEntity ? 'gsplat' : 'authored-transform',
        });
      }
      if (pointDots.isMorphDone()) {
        if (pointMorphDoneAtMs === 0) pointMorphDoneAtMs = nowMs;
        entranceTraceThrottled('morph-done', 2000, 'preview dots morph complete', {
          renderedSplats,
          gsplatAssetReady,
          loadBar: worldEntrance.loadProgress.toFixed(2),
        });
      }
    }

    const entranceShowsProgress =
      worldEntrance.phase === 'dots' ||
      worldEntrance.phase === 'loading' ||
      worldEntrance.phase === 'reveal';
    if (entranceShowsProgress) {
      worldEntrance.setAssetReady(gsplatAssetReady);
      worldEntrance.tickLoadProgress(renderedSplats, dt);
      entranceHud?.setProgress(
        resolveEntranceDisplayProgress({
          entranceProgress: worldEntrance.loadProgress,
          streamingFill: getStreamingFill(renderedSplats),
          streamingDownloadProgress: getStreamingDownloadProgress(getStreamingFill(renderedSplats)),
          streamingReady: !isStreamedSplat || streamingPhase === 'full',
          isStreamedSplat,
          entrancePhase: worldEntrance.phase,
        }),
      );
    }

    if (worldEntrance.phase === 'loading' || worldEntrance.phase === 'dots') {
      runReveal();
    }

    if (worldEntrance.phase === 'reveal') {
      requestRender(REVEAL_SETTLE_MS);

      // Fade the loading bar out smoothly as the wave takes over from the dots.
      if (worldEntrance.revealEffectTimeS >= REVEAL_PROGRESS_HOLD_S) {
        entranceHud?.hideProgress();
      }

      let entranceDone = false;

      if (pointDots) {
        // Dissolve preview points behind the expanding lift wave. The PlayCanvas shader is
        // still only tiny dots at the front; trailing the front keeps a visible overlap and
        // prevents the refined preview cloud from disappearing into a black frame.
        const canFinishPreview = canFullyDissolvePreviewDots({
          effectTime: worldEntrance.revealEffectTimeS,
          completionTime: worldEntrance.revealCompletionTimeS,
          overlapTailSeconds: PREVIEW_FINAL_OVERLAP_S,
        });
        let dissolveRadius = previewDissolveWaveRadius({
          effectTime: worldEntrance.revealEffectTimeS,
          delay: worldEntrance.revealConfig.delay,
          speed: worldEntrance.revealConfig.speed,
          acceleration: worldEntrance.revealConfig.acceleration,
          trailRadius: PREVIEW_DISSOLVE_TRAIL_RADIUS,
        });
        if (!canFinishPreview) {
          dissolveRadius = Math.min(
            dissolveRadius,
            Math.max(0, pointDots.getMaxRevealRadius() - 0.01),
          );
        }
        const fullyDissolved = pointDots.dissolveToRadius(dissolveRadius);

        if (fullyDissolved) {
          pointDots.dispose();
          pointDots = null;
          logEntrance('preview dots dissolved by wave', {
            effectTimeS: worldEntrance.revealEffectTimeS.toFixed(3),
            renderedSplats,
          });
        }
      }

      if (!entranceDone) entranceDone = worldEntrance.tick(dt);

      if (entranceDone) {
        if (pointDots) {
          pointDots.dispose();
          pointDots = null;
        }
        ensureCoarseLodOpen();
        logEntrance('entrance COMPLETE', { renderedSplats });
        if (hasRenderedSplats) markSessionWarm(options.splatUrl, graphicsApi);
        syncWorldContentVisibility();
        burstLayout();
      }
    }
  };

  const applyFullLodPreset = (resource: GsplatOctreeResource) => {
    const fullRange =
      resolveQualityLodRange(activeQuality, resource) ?? resolveLodRange(preset, resource);
    if (gsplatComp) {
      applyComponentLodRange(gsplatComp, preset, resource, fullRange);
      const cam = cameraEntity.camera;
      if (cam) applyComponentLodDistances(gsplatComp, cam, preset);
    }
    streamingPhase = 'streaming';
    coarseBootstrapDone = true;
    requestRender(BOOTSTRAP_SETTLE_MS);
    diag.log(
      `lod open ${gsplatComp ? gsplatLod(gsplatComp).lodRangeMin : '?'}–` +
        `${gsplatComp ? gsplatLod(gsplatComp).lodRangeMax : '?'} ` +
        `(octree=${resource?.octree?.lodLevels ?? '?'})`,
    );
  };

  const loadGsplat = async (url: string): Promise<pc.Entity> => {
    if (gsplatEntity) {
      gsplatEntity.destroy();
      gsplatEntity = null;
      gsplatComp = null;
    }
    if (customAsset) {
      appInstance.assets.remove(customAsset);
      customAsset.unload();
      customAsset = null;
    }

    streamingPhase = 'booting';
    coarseBootstrapDone = false;
    gsplatOctreeResource = null;
    resetStreamedSplatProgressState(streamedProgressState);
    gsplatAssetReady = false;
    gsplatAssetLoadProgress = 0;
    gsplatAssetLoadedBytes = 0;
    gsplatAssetTotalBytes = 0;
    lastGsplatProgressLogPct = -10;
    lastGsplatProgressLogMs = 0;
    revealShaderStableSinceMs = 0;
    pendingChunkLoads = 0;
    streamingStableSinceMs = 0;
    lastStreamingSplats = -1;
    loadState = 'loading';
    errorMessage = undefined;
    emitHud(0, true);

    const asset = new pc.Asset('gsplat', 'gsplat', { url });
    appInstance.assets.add(asset);

    const onProgress = (loadedBytes: number, totalBytes: number) => {
      gsplatAssetLoadedBytes = Math.max(0, loadedBytes);
      gsplatAssetTotalBytes = Math.max(0, totalBytes);
      if (gsplatAssetTotalBytes > 0) {
        gsplatAssetLoadProgress = Math.min(1, gsplatAssetLoadedBytes / gsplatAssetTotalBytes);
        worldEntrance?.setAssetLoadProgress(gsplatAssetLoadProgress);
      }

      const now = performance.now();
      const pct =
        gsplatAssetTotalBytes > 0
          ? Math.floor(gsplatAssetLoadProgress * 100)
          : lastGsplatProgressLogPct;
      const shouldLog =
        gsplatAssetTotalBytes > 0 &&
        (pct >= lastGsplatProgressLogPct + 10 || now - lastGsplatProgressLogMs >= 8_000);
      if (shouldLog) {
        lastGsplatProgressLogPct = pct;
        lastGsplatProgressLogMs = now;
        logEntrance('gsplat download progress', {
          pct,
          loaded: formatMiB(gsplatAssetLoadedBytes),
          total: formatMiB(gsplatAssetTotalBytes),
          phase: worldEntrance?.phase ?? 'none',
        });
      }
    };

    await new Promise<void>((resolve, reject) => {
      asset.on('progress', onProgress);
      asset.on('load', () => {
        asset.off('progress', onProgress);
        resolve();
      });
      asset.on('error', (err: string) => {
        asset.off('progress', onProgress);
        reject(new Error(err));
      });
      appInstance.assets.load(asset);
    });
    diag.log('gsplat asset loaded');
    gsplatAssetReady = true;
    gsplatAssetLoadProgress = 1;
    worldEntrance?.setAssetLoadProgress(1);
    worldEntrance?.setAssetReady(true);
    customAsset = asset;
    const resource = asset.resource as GsplatOctreeResource;
    gsplatOctreeResource = resource;
    const lodLevels = resource?.octree?.lodLevels;
    logEntrance('gsplat asset downloaded', {
      lodLevels: lodLevels ?? 0,
      entrancePhase: worldEntrance?.phase ?? 'none',
    });
    let lodBootstrapActive = false;

    gsplatEntity = new pc.Entity('gsplat');
    const cam = cameraEntity.camera!;
    gsplatEntity.addComponent('gsplat', {
      asset,
      unified: true,
      lodBaseDistance: preset.lodBaseDistance * computeGsplatFovScale(cam),
      lodMultiplier: preset.lodMultiplier,
    });
    const [ex, ey, ez] = resolveGsplatEuler(options.worldScene, preset);
    const ws = options.worldScene;
    if (ws) {
      gsplatEntity.setLocalPosition(ws.position[0], ws.position[1], ws.position[2]);
      gsplatEntity.setLocalScale(ws.scale[0], ws.scale[1], ws.scale[2]);
    }
    gsplatEntity.setLocalEulerAngles(ex, ey, ez);
    gsplatEntity.enabled = true;
    appInstance.root.addChild(gsplatEntity);
    gsplatComp = gsplatEntity.gsplat!;
    // Pop-free entrance: the gsplat stays on the WORLD layer and enabled the whole time.
    // The reveal script is attached + enabled immediately but held at effectTime 0, which
    // scales every splat to zero — so the splats are present and warming on the GPU yet
    // fully invisible, with no hidden-layer swap and no frame of un-revealed colour.
    setGsplatLayers(gsplatEntity, [pc.LAYERID_WORLD]);
    if (worldEntrance && worldEntrance.phase !== 'done') {
      const revealCenter = resolveRevealCenterForRenderer();
      worldEntrance.attachRevealScript(gsplatEntity, revealCenter.center);
      requestRender(BOOTSTRAP_SETTLE_MS);
      logEntrance('gsplat entity created (held reveal, world layer)', {
        enabled: gsplatEntity.enabled,
        streamed: isStreamedSplat,
        focusSpace: revealCenter.space,
        focusCenter: `${revealCenter.center.x.toFixed(2)},${revealCenter.center.y.toFixed(2)},${revealCenter.center.z.toFixed(2)}`,
      });
    }

    if (lodLevels && lodLevels > 0 && isStreamedSplat) {
      const gs = gsplatLod(gsplatComp);
      const bootstrapRange = resolveBootstrapLodRange(
        preset,
        resource,
        activeQuality,
        isMobileDevice,
      );
      gs.lodRangeMin = bootstrapRange.min;
      gs.lodRangeMax = bootstrapRange.max;
      lodBootstrapActive = true;
    } else {
      const activeRange = resolveQualityLodRange(activeQuality, resource);
      applyComponentLodRange(gsplatComp, preset, resource, activeRange);
    }

    if (lodBootstrapActive && isStreamedSplat) {
      const gsplatSystem = appInstance.systems.gsplat as pc.GSplatComponentSystem;
      const onFrameReady = (
        _cam: pc.Camera,
        _layer: pc.Layer,
        ready: boolean,
        loadingCount: number,
      ) => {
        const completedRefinementStep = pendingChunkLoads > 0 && loadingCount === 0;
        pendingChunkLoads = loadingCount;
        const sig = `ready=${ready} pending=${loadingCount}`;
        if (sig !== lastFrameReadySig) {
          lastFrameReadySig = sig;
          diag.log(`FRAMEREADY ${sig}`);
        }
        const now = performance.now();
        if (loadingCount > 0 && !ready) {
          if (stuckPendingSinceMs === 0 || loadingCount !== lastStuckPending) {
            stuckPendingSinceMs = now;
            lastStuckPending = loadingCount;
          } else if (now - stuckPendingSinceMs >= 12_000) {
            diag.log(`STUCK ${loadingCount} chunk loads for 12s+ (WebGPU texture upload?)`);
            stuckPendingSinceMs = now;
          }
        } else {
          stuckPendingSinceMs = 0;
          lastStuckPending = -1;
        }

        if (!coarseBootstrapDone && ready && loadingCount === 0) {
          applyFullLodPreset(resource);
        } else {
          tryCompleteStreaming(appInstance.stats.frame.gsplats ?? 0, ready, loadingCount);
        }

        // Octree refinement is intentionally incremental: after one file arrives, the next
        // finer file is discovered on a later render. Keep a short render window after every
        // completed step or render-on-demand can idle at LOD1 while the desired LOD0 is pending
        // discovery. This also makes "pause near a wall" converge instead of freezing quality.
        if (completedRefinementStep && coarseBootstrapDone) {
          requestRender(1_500);
        }

        if (loadingCount > 0 || !ready || streamingPhase !== 'full') {
          requestRender(BOOTSTRAP_SETTLE_MS);
        }
      };
      gsplatSystem.on(pc.GSplatComponentSystem.EVENT_FRAMEREADY, onFrameReady);
    } else {
      applyFullLodPreset(resource);
    }

    streamingPhase = 'streaming';
    loadState = 'ready';
    requestRender(BOOTSTRAP_SETTLE_MS);
    emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    return gsplatEntity;
  };

  appInstance.on('update', (dt: number) => {
    const renderedSplats = appInstance.stats.frame.gsplats ?? 0;
    const nowMs = performance.now();
    flythroughController?.tick(nowMs);
    const entranceActive = worldEntrance?.isActive ?? false;
    spatialPrompt.setReady(isWorldInteractiveReady(), entranceActive, nowMs);
    spatialPrompt.setToolActive(
      measureTool.isActive() || photoTool.isActive() || (flythroughController?.isActive() ?? false),
      nowMs,
    );
    const spatialPromptState = spatialPrompt.tick(nowMs);
    if (spatialPromptState.visibleChanged) {
      if (spatialPromptState.visible) {
        captureSpatialPromptDemoAnchor();
      } else {
        spatialPromptDemoAnchorActive = false;
      }
      requestRender();
    }
    if (spatialPromptState.demoState === 'running') {
      applySpatialControlDemoAction(
        spatialPromptState.demoAction,
        Math.min(Math.max(dt, 0), 1 / 20),
        spatialPromptState.demoState,
      );
    }
    const pos = cameraEntity.getLocalPosition();
    const rot = cameraEntity.getLocalRotation();

    const visible = pageVisible && (skipIntersectionGate || canvasOnScreen || entranceActive);

    // Render-on-demand: only keep drawing while something is actually changing.
    // Camera moved? (position or orientation; NaN seeds the baseline on the first frame)
    const camMoved =
      Number.isNaN(lastCam.x) ||
      Math.abs(pos.x - lastCam.x) > 1e-4 ||
      Math.abs(pos.y - lastCam.y) > 1e-4 ||
      Math.abs(pos.z - lastCam.z) > 1e-4 ||
      Math.abs(rot.x - lastCam.rx) > 1e-6 ||
      Math.abs(rot.y - lastCam.ry) > 1e-6 ||
      Math.abs(rot.z - lastCam.rz) > 1e-6 ||
      Math.abs(rot.w - lastCam.rw) > 1e-6;
    if (camMoved) {
      lastCam.x = pos.x;
      lastCam.y = pos.y;
      lastCam.z = pos.z;
      lastCam.rx = rot.x;
      lastCam.ry = rot.y;
      lastCam.rz = rot.z;
      lastCam.rw = rot.w;
      requestRender();
    }
    // Scene still streaming / LOD re-settling? (gsplat count meaningfully changing).
    // Threshold avoids per-frame sort jitter keeping the GPU awake forever.
    const splatsChanged = Math.abs(renderedSplats - lastSettledSplats) > 2000;
    if (splatsChanged) {
      lastSettledSplats = renderedSplats;
      requestRender();
    }
    // Animating world content (looping GLB clip, playing video) that is actually on-screen,
    // or a hover change. Off-screen animations keep advancing but don't force a redraw.
    if (
      visible &&
      (camMoved || nowMs - lastOverlayAnimationCheckMs >= 1000 / ANIMATED_CONTENT_FPS)
    ) {
      lastOverlayAnimationCheckMs = nowMs;
      overlayAnimating = isAnimatingContentVisible();
    } else if (!visible) {
      overlayAnimating = false;
    }
    const hoverChanged = overlay?.consumeHoverDirty() ?? false;
    const bootstrapActive = needsBootstrapRender();
    if (bootstrapActive) requestRender(BOOTSTRAP_SETTLE_MS);
    if (camMoved || splatsChanged || hoverChanged) {
      requestRender();
    }
    if (
      overlayAnimating &&
      nowMs - lastAnimatedContentRenderRequestMs >= 1000 / ANIMATED_CONTENT_FPS
    ) {
      lastAnimatedContentRenderRequestMs = nowMs;
      requestRender(1);
    }

    // Draw only while a render is requested (and not paused), capped to the single target
    // frame rate. Bootstrap mode keeps rendering until splats are actually on screen.
    const renderRequested = bootstrapActive || entranceActive || nowMs <= renderUntilMs;
    const fpsAllowed = !hasRenderedSplats || nowMs - lastRenderMs >= minFrameIntervalMs;
    const shouldRender = visible && renderRequested && fpsAllowed;

    if (diagEnabled && needsBootstrapRender() && nowMs - lastDiagHeartbeatMs >= 2000) {
      lastDiagHeartbeatMs = nowMs;
      diag.log(
        `boot load=${loadState} stream=${streamingPhase} pending=${pendingChunkLoads} ` +
          `entrance=${worldEntrance?.phase ?? '—'} splats=${renderedSplats} ok=${hasRenderedSplats} ` +
          `canvas=${canvas.clientWidth}x${canvas.clientHeight} vis=${visible} req=${renderRequested} draw=${shouldRender}`,
      );
      emitHud(renderedSplats, true);
    }

    if (entranceActive && worldEntrance) {
      tickEntranceSequence(dt, nowMs, renderedSplats);
      requestRender();
    }

    const worldContentVisibilityChanged = syncWorldContentVisibility();

    if (overlay && isWorldInteractiveReady()) {
      const placingMeasure = measureTool.isPlacing();
      const pointerLockedChanged = pointerLocked !== lastWorldChromePointerLocked;
      const placingChanged = placingMeasure !== lastWorldChromePlacing;
      const updateWorldChrome = shouldUpdateWorldChrome({
        firstUpdate: worldChromeFirstUpdate,
        cameraMoved: camMoved,
        shouldRender,
        overlayAnimating,
        hoverChanged,
        visibilityChanged: worldContentVisibilityChanged,
        pointerLockedChanged,
        placingChanged,
      });

      overlay.tick(
        dt,
        pos,
        shouldRender,
        shouldUpdateWorldZones({
          firstUpdate: worldChromeFirstUpdate,
          cameraMoved: camMoved,
          visibilityChanged: worldContentVisibilityChanged,
        }),
      );
      if (updateWorldChrome) {
        touchReticleEl.style.display = isTouchMode && !placingMeasure ? '' : 'none';
        crosshairEl.style.display = pointerLocked && !isTouchMode && !placingMeasure ? '' : 'none';
        overlay.updateLabels(cameraEntity);
        if (
          !placingMeasure &&
          shouldUpdateInteractableHover({
            firstUpdate: worldChromeFirstUpdate,
            cameraMoved: camMoved,
            visibilityChanged: worldContentVisibilityChanged,
            pointerLockedChanged,
            placingChanged,
          })
        ) {
          overlay.updateInteractableHover(
            cameraEntity,
            nowMs,
            worldChromeFirstUpdate || worldContentVisibilityChanged || pointerLockedChanged,
          );
        }
        const hasTapTarget = overlay.hasTapTarget();
        touchReticleEl.classList.toggle('crosshair--target', hasTapTarget);
        crosshairEl.classList.toggle('crosshair--target', hasTapTarget);
        worldChromeFirstUpdate = false;
        lastWorldChromePointerLocked = pointerLocked;
        lastWorldChromePlacing = placingMeasure;
      }
    } else if (isTouchMode) {
      touchReticleEl.style.display = 'none';
      touchReticleEl.classList.remove('crosshair--target');
      crosshairEl.classList.remove('crosshair--target');
      worldChromeFirstUpdate = true;
    }

    if (measureTool.needsTick()) {
      measureTool.tick();
    }

    if (shouldApplyCanvasLayoutOnFrame({ layoutDirty: canvasLayoutDirty, bootstrapActive })) {
      applyCanvasLayout();
    }

    if (shouldRender) {
      appInstance.renderNextFrame = true;
      // Use wall-clock time between rendered frames so FPS is correct even when the
      // render rate is capped below the rAF rate (per-rAF dt would overestimate it).
      const sinceLastRenderS = lastRenderMs > 0 ? (nowMs - lastRenderMs) / 1000 : dt;
      lastRenderMs = nowMs;

      const frameStats = appInstance.stats.frame as { gsplats?: number; gsplatTime?: number };
      if (typeof frameStats.gsplatTime === 'number') {
        lastGsplatWorkMs = frameStats.gsplatTime;
        renderHistory.push(lastGsplatWorkMs);
        if (renderHistory.length > 60) renderHistory.shift();
      }
      const revealStarted =
        worldEntrance?.phase === 'reveal' ||
        worldEntrance?.phase === 'done' ||
        !worldEntrance?.isActive;
      if (!hasRenderedSplats && revealStarted && renderedSplats >= BOOTSTRAP_SPLAT_THRESHOLD) {
        logEntrance('first splats rendered', {
          count: renderedSplats,
          phase: worldEntrance?.phase ?? 'none',
          scriptEnabled: worldEntrance?.revealScriptEnabled ?? false,
        });
        hasRenderedSplats = true;
      }

      if (coarseBootstrapDone && streamingPhase !== 'full') {
        tryCompleteStreaming(renderedSplats, pendingChunkLoads === 0, pendingChunkLoads);
      }

      // FPS reflects actual rendered frames (the update loop itself runs every rAF).
      hudAccumulatedSeconds += sinceLastRenderS;
      hudAccumulatedFrames += 1;
      if (hudAccumulatedSeconds >= 0.5) {
        fps = hudAccumulatedFrames / hudAccumulatedSeconds;
        frameTimeMs = 1000 / Math.max(fps, 0.0001);
        hudAccumulatedSeconds = 0;
        hudAccumulatedFrames = 0;
      }

      // Detailed stats are only needed for the debug HUD; skip the work otherwise.
      if (app && miniStats) {
        app.stats.updateDetailed(app.renderer, device);
      }
    }

    emitHud(renderedSplats);
  });

  appInstance.start();
  diag.log('app.start');

  const bootstrapTimer = window.setTimeout(() => {
    if (disposed || hasRenderedSplats || loadState === 'error') return;
    if (worldEntrance?.isActive) return;
    if (!gsplatLoadStarted) beginGsplatLoad();
    if (!hasRenderedSplats && gsplatLoadStarted) {
      loadState = 'error';
      errorMessage =
        'The scene did not display on this device. Try refreshing, or use Chrome/Firefox on desktop.';
      diag.log('BOOTSTRAP TIMEOUT — no splats rendered');
      console.error(
        '[GII] Bootstrap timeout — no splats rendered after',
        BOOTSTRAP_TIMEOUT_MS,
        'ms',
      );
      entranceHud?.hideWelcome();
      entranceHud?.hideProgress();
      emitHud(0, true);
    }
  }, BOOTSTRAP_TIMEOUT_MS);

  const loadEntrancePreviewAssets = () => {
    void loadLogoFormation(ENTRANCE_LOGO_URL, ENTRANCE_DOT_COUNT)
      .then((formation) => {
        if (disposed) return;
        entranceLogoFormation = formation;
        diag.log(`logo ${formation.points.length.toLocaleString()} entrance dots`);
        tryStartPointDots();
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        diag.log(`logo skip ${msg}`);
      });

    const previewUrl = options.worldScene?.pointPreview;
    if (!previewUrl) return;

    void loadPcvPreview(previewUrl)
      .then((data) => {
        if (disposed) return;
        const { positions, count } = subsamplePcvPositions(
          data.positions,
          data.count,
          ENTRANCE_DOT_COUNT,
        );
        entrancePcvPositions = positions;
        diag.log(
          `pcv ${count.toLocaleString()} preview dots (from ${data.count.toLocaleString()})`,
        );
        tryStartPointDots();
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        diag.log(`pcv skip ${msg}`);
        console.error('[GII entrance] pcv load failed:', err);
      });
  };

  const bootstrapEntrance = () => {
    // Phase 1 — splat loads in the background. Warm candidates also preload the
    // lightweight dot assets so a cache miss can fall back without a black wait.
    beginGsplatLoad();
    loadEntrancePreviewAssets();
  };

  bootstrapEntrance();

  return {
    resize: burstLayout,
    setDebugPerf,
    setOverlayTool: (tool) => {
      if (isTouchMode && tool === 'measure') return;
      measureTool.setEditMode(tool === 'measure');
      if (tool === 'measure' && document.pointerLockElement === canvas) {
        document.exitPointerLock();
      }
      requestRender(BOOTSTRAP_SETTLE_MS);
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
    toggleMeasureEdit: () => {
      if (isTouchMode) return;
      measureTool.toggleEditMode();
      if (measureTool.isEditMode() && document.pointerLockElement === canvas) {
        document.exitPointerLock();
      }
      requestRender(BOOTSTRAP_SETTLE_MS);
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
    setQuality: applyRuntimeQuality,
    setMediaAutoplay: (enabled) => {
      overlay?.setVideoAutoplay(enabled);
      requestRender(BOOTSTRAP_SETTLE_MS);
      emitHud(appInstance.stats.frame.gsplats ?? 0, true);
    },
    getCameraAuthoringSnapshot: () => readCameraAuthoringSnapshot(),
    capturePng: capturePngImpl,
    dispose: () => {
      disposed = true;
      hidePointerLockNotice();
      window.clearTimeout(crosshairClickTimer);
      canvas.removeEventListener('mousedown', onCrosshairMouseDown);
      setDebugPerf(false);
      detachControls?.();
      window.removeEventListener('keydown', onLodDebugKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      worldEntrance?.dispose();
      entranceHud?.dispose();
      spatialPrompt.dispose();
      pointDots?.dispose();
      resetStreamedSplatProgressState(streamedProgressState);
      entrancePcvPositions = null;
      overlay?.dispose();
      flythroughController?.dispose();
      photoTool.dispose();
      measureTool.dispose();
      cameraFrame.destroy();
      collision.dispose();
      ro.disconnect();
      io.disconnect();
      window.clearTimeout(bootstrapTimer);
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onRejection);
      diag.dispose();
      entranceLogoFormation = null;
      if (gsplatEntity) gsplatEntity.destroy();
      if (customAsset) {
        appInstance.assets.remove(customAsset);
        customAsset.unload();
      }
      appInstance.destroy();
      canvas.remove();
    },
  };
}
